import { useState, useMemo, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useSearchParams, Link } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  Wrench, Globe, FileText, Code, Smartphone, Shield, Zap, Languages,
  Search, Loader2, CheckCircle, XCircle, AlertTriangle, ChevronDown, ChevronUp,
  ExternalLink, ArrowRight, BarChart3, Cpu, Accessibility, Cookie, Lock,
  Link2, GitCompareArrows, Bot, X, BookOpen, MousePointerClick, Gauge,
  Activity, ShieldCheck, Database, Sparkles, MessageSquare, PenLine, HelpCircle, Share2
} from 'lucide-react'
import { clsx } from 'clsx'
import { useTranslation } from 'react-i18next'
import api from '../lib/api'
import { normalizeUrlInput, isValidUrl } from '../lib/url'
import { toast } from '../lib/toast'
import { useAuthStore } from '../store/authStore'
import { useKnowledge } from '../components/KnowledgeProvider'
import { hasKb, TOOL_KB } from '../data/knowledgeBase'
import { extractFindings, howToFromKb, downloadComprehensiveReport, isPaidPlan, type ReportSection } from '../lib/reportPdf'
import SubscriptionGate from '../components/SubscriptionGate'
import WebsitePicker from '../components/WebsitePicker'
import { FileDown } from 'lucide-react'
import { TOOL_PAGES, toolH1 } from '../data/toolPages'
import { toolPath } from '../lib/localizedUrl'
import { useFocusTrap } from '../lib/useFocusTrap'
import AiToolPanel from '../components/AiToolPanel'
import { useSeoHead, SITE } from '../lib/seo'
import ShareSnapshotModal from '../components/ShareSnapshotModal'
import Turnstile, { turnstileEnabled } from '../components/Turnstile'
import Confetti from '../components/Confetti'
import { PublicToolUsageCard } from '../components/ToolUsageStats'
import ToolRating, { RatingBadge } from '../components/ToolRating'

type ToolId = 'robots' | 'sitemap' | 'schema' | 'serp' | 'hreflang' | 'mobile' | 'security' | 'pagespeed' | 'lighthouse' | 'crux' | 'safe-browsing' | 'indexation' | 'redirects' | 'content' | 'technology' | 'accessibility' | 'wcag' | 'ux' | 'cookies' | 'dns' | 'broken-links' | 'compare' | 'bulk' | 'geo' | 'llms-txt' | 'ai-citation' | 'geo-rewrite' | 'ai-answer'

// AI nástroje (BYOK — vlastní klíč). Mají vlastní panel (AiToolPanel) místo URL formuláře.
const AI_TOOLS: ToolId[] = ['ai-citation', 'geo-rewrite', 'ai-answer']
const isAiTool = (id: ToolId): id is import('../components/AiToolPanel').AiToolId => AI_TOOLS.includes(id)

const TOOL_ICONS: Record<ToolId, any> = {
  robots: FileText, sitemap: Globe, schema: Code, serp: Search, hreflang: Languages,
  mobile: Smartphone, security: Shield, pagespeed: Zap, lighthouse: Gauge,
  crux: Activity, 'safe-browsing': ShieldCheck, indexation: Database, redirects: ArrowRight,
  content: BarChart3, technology: Cpu, accessibility: Accessibility, wcag: ShieldCheck, ux: MousePointerClick, cookies: Cookie,
  dns: Lock, 'broken-links': Link2, compare: GitCompareArrows, bulk: FileText, geo: Bot, 'llms-txt': Sparkles,
  'ai-citation': MessageSquare, 'geo-rewrite': PenLine, 'ai-answer': HelpCircle,
}
const TOOL_IDS: ToolId[] = ['robots', 'sitemap', 'schema', 'serp', 'hreflang', 'mobile', 'security', 'pagespeed', 'lighthouse', 'crux', 'safe-browsing', 'indexation', 'redirects', 'content', 'technology', 'accessibility', 'wcag', 'ux', 'cookies', 'dns', 'broken-links', 'compare', 'bulk', 'geo', 'llms-txt', 'ai-citation', 'geo-rewrite', 'ai-answer']

// Ze „Spustit vše" vynecháváme nástroje, které potřebují JINÝ vstup než jednu URL nebo jsou těžké:
// compare = 2 weby, bulk = seznam URL, llms-txt = generátor, AI nástroje = BYOK + vlastní formulář.
const RUN_ALL_SKIP: ToolId[] = ['compare', 'bulk', 'llms-txt', 'ai-citation', 'geo-rewrite', 'ai-answer']

function useTools() {
  const { t } = useTranslation()
  return TOOL_IDS.map((id) => {
    const key = id.replace('-', '_')
    return { id, name: t(`tools.${key}.name`), desc: t(`tools.${key}.desc`), icon: TOOL_ICONS[id] }
  })
}

export default function ToolsPage() {
  const { t, i18n } = useTranslation()
  useSeoHead({
    title: t('tools.meta_title', { defaultValue: 'SEO nástroje zdarma: 26 nástrojů na kontrolu webu | SEO Tools' }),
    description: t('tools.meta_desc', { defaultValue: 'robots.txt, sitemap, rychlost, strukturovaná data, hreflang, GEO a další. 26 SEO nástrojů zdarma a bez registrace.' }),
    canonicalPath: '/tools',
    // ItemList odpovídá viditelnému gridu nástrojů (schema musí sedět na obsah stránky) → carousel/rich výsledky.
    jsonLd: [
      {
        '@context': 'https://schema.org', '@type': 'CollectionPage',
        name: t('tools.meta_title', { defaultValue: 'SEO nástroje zdarma' }),
        url: SITE + '/tools',
        mainEntity: {
          '@type': 'ItemList',
          numberOfItems: TOOL_IDS.length,
          itemListElement: TOOL_IDS.map((id, i) => ({
            '@type': 'ListItem', position: i + 1,
            name: t(`tools.${id.replace('-', '_')}.name`),
          })),
        },
      },
      {
        '@context': 'https://schema.org', '@type': 'BreadcrumbList',
        itemListElement: [{ '@type': 'ListItem', position: 1, name: t('nav.tools', { defaultValue: 'Nástroje' }), item: SITE + '/tools' }],
      },
    ],
  })
  const { isAuthenticated, user } = useAuthStore()
  const [searchParams] = useSearchParams()
  const { open: openKb } = useKnowledge()
  const [gateOpen, setGateOpen] = useState(false)
  const [limitReached, setLimitReached] = useState<string | null>(null)
  const [exportingPdf, setExportingPdf] = useState(false)
  const [shareOpen, setShareOpen] = useState(false)
  const [shareAllOpen, setShareAllOpen] = useState(false)
  const TOOLS = useTools()
  // Souhrny hodnocení všech nástrojů (pro hvězdičky na kartách)
  const { data: ratingsAll } = useQuery<Record<string, { average: number; count: number }>>({
    queryKey: ['tool-ratings-all'],
    queryFn: () => api.get('/tools/ratings').then((r) => r.data.ratings || {}),
    staleTime: 300_000,
  })
  const [activeTool, setActiveTool] = useState<ToolId>('robots')
  const [url, setUrl] = useState('')
  const [url2, setUrl2] = useState('')
  const [bulkUrls, setBulkUrls] = useState('')
  const [result, setResult] = useState<any>(null)
  const resultRef = useRef<HTMLDivElement>(null)
  // Po výběru výsledku (zejm. na mobilu) k němu plynule odscrolluj, ať ho uživatel vidí.
  const scrollToResult = () => setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60)
  const [allResults, setAllResults] = useState<Record<string, any> | null>(null)
  const [runningAll, setRunningAll] = useState(false)
  const [currentTool, setCurrentTool] = useState<ToolId | null>(null)
  // Pokročilé nástroje (vyžadují další vstup) — popup
  const [extra, setExtra] = useState<null | 'compare' | 'bulk'>(null)
  const [extraUrl2, setExtraUrl2] = useState('')
  const [extraUrls, setExtraUrls] = useState('')

  // Focus trap pro modaly (WCAG 2.1.2 / 2.4.3)
  const extraModalRef = useRef<HTMLDivElement>(null)
  useFocusTrap(extraModalRef, () => setExtra(null), !!extra)
  const limitModalRef = useRef<HTMLDivElement>(null)
  useFocusTrap(limitModalRef, () => setLimitReached(null), !!limitReached)

  // Jednorázová „nejsi robot?" brána pro anonymy (Turnstile). Po vyřešení backend
  // odemkne IP na 12 h → další běhy nástrojů projdou bez další CAPTCHy.
  // Přihlášení a vypnutý Turnstile bránu vůbec neřeší.
  const humanOkRef = useRef(false)
  const [humanGate, setHumanGate] = useState<null | { resolve: (ok: boolean) => void }>(null)
  const [humanBusy, setHumanBusy] = useState(false)
  const humanGateRef = useRef<HTMLDivElement>(null)
  const closeHumanGate = (ok: boolean) => { humanGate?.resolve(ok); setHumanGate(null) }
  useFocusTrap(humanGateRef, () => closeHumanGate(false), !!humanGate)

  const ensureHuman = (): Promise<boolean> => {
    if (isAuthenticated || !turnstileEnabled()) return Promise.resolve(true)
    if (humanOkRef.current || sessionStorage.getItem('tools_human_ok') === '1') return Promise.resolve(true)
    return new Promise<boolean>((resolve) => setHumanGate({ resolve }))
  }

  const submitHuman = async (token: string) => {
    if (!token) return
    setHumanBusy(true)
    try {
      await api.post('/tools/verify-human', { turnstile_token: token })
      humanOkRef.current = true
      sessionStorage.setItem('tools_human_ok', '1')
      closeHumanGate(true)
    } catch {
      toast.error(t('tools.human_failed', { defaultValue: 'Ověření selhalo, zkuste to prosím znovu.' }))
    } finally {
      setHumanBusy(false)
    }
  }

  // Load user projects if authenticated
  const { data: projects } = useQuery({
    queryKey: ['rank-projects'],
    queryFn: () => api.get('/rank/projects').then((r) => r.data.projects),
    enabled: isAuthenticated,
  })

  const { data: websites } = useQuery({
    queryKey: ['websites'],
    queryFn: () => api.get('/websites').then((r) => r.data.websites),
    enabled: isAuthenticated,
  })

  // Sjednocený seznam pro WebsitePicker (weby + rank projekty bez duplicit)
  const pickerSites = useMemo(() => {
    const ws = (websites ?? []).map((w: any) => ({ id: `w-${w.id}`, domain: w.domain, name: w.name, description: w.description, favicon_url: w.favicon_url }))
    const ps = (projects ?? [])
      .filter((p: any) => !ws.some((w: any) => w.domain === p.domain))
      .map((p: any) => ({ id: `p-${p.id}`, domain: p.domain, name: p.name }))
    return [...ws, ...ps]
  }, [websites, projects])

  const runTool = useMutation({
    mutationFn: async ({ tool, url, url2, urls, refresh }: { tool: ToolId; url: string; url2?: string; urls?: string; refresh?: boolean }) => {
      // Anonym musí jednou projít Turnstile branou (pak je IP odemčená 12 h).
      if (!(await ensureHuman())) throw new Error('human_cancelled')
      // Cache řeší backend: levné nástroje má přihlášený vždy čerstvé, drahé (Google kvóta) z cache.
      // `refresh` (tlačítko „Přeměřit") vynutí čerstvé měření i u drahých.
      const payload: any = { url, ...(refresh ? { refresh: true } : {}) }
      if (tool === 'compare' && url2) payload.url2 = url2
      if (tool === 'bulk' && urls) { payload.urls = urls; delete payload.url }
      const { data } = await api.post(`/tools/${tool}`, payload)
      return data
    },
    onSuccess: (data, variables) => {
      setResult(data)
      if (!runningAll) scrollToResult() // jednotlivý běh → odscrolluj k výsledku (mobil)
      // Ulož do Historie nástrojů (jen pro přihlášené, jen úspěšné výsledky s URL)
      if (isAuthenticated && variables.url && !data?.error) {
        api.post('/tool-runs', { tool: variables.tool, url: variables.url, result: data }).catch(() => {})
      }
    },
    onError: (err: any) => {
      if (err?.message === 'human_cancelled') return // uživatel zavřel Turnstile bránu
      if (err?.response?.status === 429 && err?.response?.data?.limit_reached) {
        setLimitReached(err.response.data.message || t('common.invalid_url'))
      } else if (err?.response?.status === 403 && err?.response?.data?.turnstile_required) {
        // IP marker vypršel / jiná IP → resetuj a nech uživatele znovu projít branou
        humanOkRef.current = false
        sessionStorage.removeItem('tools_human_ok')
        ensureHuman()
      }
    },
  })

  // Re-spuštění konkrétního nástroje z Historie přes ?tool=…&url=…
  useEffect(() => {
    const qpTool = searchParams.get('tool')
    const qpUrl = searchParams.get('url')
    if (qpTool && qpUrl && TOOLS.some((t) => t.id === qpTool)) {
      setActiveTool(qpTool as ToolId)
      setUrl(qpUrl)
      setResult(null)
      setAllResults(null)
      runTool.mutate({ tool: qpTool as ToolId, url: qpUrl })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (activeTool === 'bulk') {
      if (!bulkUrls.trim()) return
      // bulk: aspoň jedna platná URL v seznamu
      const list = bulkUrls.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean)
      if (!list.some(isValidUrl)) { toast.error(t('common.invalid_url')); return }
      setResult(null); setAllResults(null)
      runTool.mutate({ tool: 'bulk', url: '', urls: bulkUrls.trim() })
      return
    }
    if (!isValidUrl(url)) { toast.error(t('common.invalid_url')); return }
    if (activeTool === 'compare' && !isValidUrl(url2)) { toast.error(t('common.invalid_url')); return }
    // Oprav poškozené/duplicitní scheme (https//, https://https//, chybějící dvojtečka) přímo v inputu
    const cleanUrl = normalizeUrlInput(url)
    if (cleanUrl !== url.trim()) setUrl(cleanUrl)
    const cleanUrl2 = url2.trim() ? normalizeUrlInput(url2) : undefined
    setResult(null)
    setAllResults(null)
    runTool.mutate({ tool: activeTool, url: cleanUrl, url2: cleanUrl2, urls: bulkUrls.trim() || undefined })
  }

  const runAllTools = async (targetUrl: string) => {
    if (!isValidUrl(targetUrl)) { toast.error(t('common.invalid_url')); return }
    if (!(await ensureHuman())) return // anonym → jednorázová Turnstile brána
    targetUrl = normalizeUrlInput(targetUrl)
    setUrl(targetUrl)
    setRunningAll(true)
    setAllResults({})
    setResult(null)

    for (const tool of TOOLS) {
      if (RUN_ALL_SKIP.includes(tool.id)) continue // přeskoč těžké/speciální nástroje v „Spustit vše"
      setCurrentTool(tool.id)
      try {
        const { data } = await api.post(`/tools/${tool.id}`, { url: targetUrl })
        setAllResults((prev) => ({ ...prev, [tool.id]: { data, status: 'done' } }))
        // Ulož do Historie nástrojů — stejně jako jednotlivý běh (jinak „Spustit vše" nic neukládalo)
        if (isAuthenticated && !data?.error) {
          api.post('/tool-runs', { tool: tool.id, url: targetUrl, result: data }).catch(() => {})
        }
      } catch (err: any) {
        // Vyčerpaný denní limit (nepřihlášený) → ukaž registraci a ukonči hromadný běh
        if (err?.response?.status === 429 && err?.response?.data?.limit_reached) {
          setLimitReached(err.response.data.message)
          break
        }
        setAllResults((prev) => ({ ...prev, [tool.id]: { error: err.message, status: 'error' } }))
      }
    }
    setCurrentTool(null)
    setRunningAll(false)
  }

  // Spustí pokročilý nástroj (compare/bulk) z popupu s vyplněným vstupem
  const runExtra = () => {
    if (extra === 'compare') {
      if (!isValidUrl(url) || !isValidUrl(extraUrl2)) { toast.error(t('common.invalid_url')); return }
      setActiveTool('compare'); setUrl2(normalizeUrlInput(extraUrl2)); setResult(null)
      runTool.mutate({ tool: 'compare', url: normalizeUrlInput(url), url2: normalizeUrlInput(extraUrl2) })
    } else if (extra === 'bulk') {
      const list = extraUrls.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean)
      if (!list.some(isValidUrl)) { toast.error(t('common.invalid_url')); return }
      setActiveTool('bulk'); setBulkUrls(extraUrls.trim()); setResult(null)
      runTool.mutate({ tool: 'bulk', url: '', urls: extraUrls.trim() })
    }
    setExtra(null)
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 120)
  }

  // Export komplexního PDF z výsledků „Spustit vše" (prémiová funkce — Starter+)
  const handleExportPdf = async () => {
    if (!isPaidPlan(user?.plan)) { setGateOpen(true); return }
    if (!allResults) return
    const sections: ReportSection[] = []
    let scoreSum = 0, scoreCount = 0
    for (const id of TOOL_IDS) {
      if (RUN_ALL_SKIP.includes(id)) continue
      const r = allResults[id]?.data
      if (!r || r.error) continue
      if (typeof r.score === 'number') { scoreSum += r.score; scoreCount++ }
      sections.push({
        name: t(`tools.${id.replace('-', '_')}.name`),
        score: typeof r.score === 'number' ? r.score : null,
        findings: extractFindings(r),
        howTo: howToFromKb(TOOL_KB[id]),
      })
    }
    const domain = url.trim().replace(/^https?:\/\//, '').replace(/\/.*$/, '')
    setExportingPdf(true)
    try {
      await downloadComprehensiveReport({
        title: t('tools.full_analysis'),
        url: url.trim(),
        domain,
        score: scoreCount ? Math.round(scoreSum / scoreCount) : null,
        sections,
      }, 'tools-analyza')
    } catch (e: any) {
      if (e?.response?.status === 403) setGateOpen(true)
    } finally {
      setExportingPdf(false)
    }
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
        <Wrench className="w-6 h-6" />
        {t('tools.title')}
      </h1>

      {/* Výběr webu — dropdown s vyhledáváním, screenshotem a SERP výpisem */}
      {isAuthenticated && pickerSites.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-6">
          <p className="text-sm text-gray-500 mb-2">{t('tools.your_projects')}</p>
          <WebsitePicker
            sites={pickerSites}
            value={url}
            onSelect={(d) => { setUrl(d); setResult(null); setAllResults(null) }}
            onRunAll={(d) => runAllTools(d)}
            placeholder={t('tools.choose_web')}
          />
        </div>
      )}

      {/* All tools running panel */}
      {allResults && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2 min-w-0">
              {runningAll && <Loader2 className="w-4 h-4 animate-spin shrink-0" />}
              <span className="truncate">{t('tools.full_analysis')} {url}</span>
            </h3>
            {!runningAll && (
              <div className="flex items-center gap-2 shrink-0 w-full sm:w-auto">
                <button
                  onClick={() => setShareAllOpen(true)}
                  className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 border border-gray-200 dark:border-white/15 text-gray-700 dark:text-gray-200 px-4 py-2 rounded-xl text-sm font-semibold hover:bg-gray-50 dark:hover:bg-white/5 transition"
                >
                  <Share2 className="w-4 h-4" />
                  {t('snapshot.share_btn', { defaultValue: 'Sdílet' })}
                </button>
                <button
                  onClick={handleExportPdf}
                  disabled={exportingPdf}
                  className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 bg-gradient-to-r from-primary-600 to-primary-500 text-white px-4 py-2 rounded-xl text-sm font-semibold hover:shadow-lg hover:shadow-primary-500/25 transition disabled:opacity-60"
                >
                  {exportingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
                  {t('analysis.export_pdf')}
                </button>
              </div>
            )}
          </div>
          {!runningAll && (
            <p className="text-xs text-gray-500 dark:text-gray-400 -mt-2 mb-4">
              {t('tools.full_analysis_note', {
                count: TOOLS.filter((x) => !RUN_ALL_SKIP.includes(x.id)).length,
                defaultValue: 'Ověřeno {{count}} nástrojů. Porovnání webů a Hromadná kontrola (potřebují jiný vstup) a AI nástroje (vyžadují vlastní AI klíč) spouštíte samostatně.',
              })}
            </p>
          )}
          <ShareSnapshotModal
            open={shareAllOpen}
            onClose={() => setShareAllOpen(false)}
            type="tool"
            title={t('tools.full_analysis') + (url ? ' — ' + url : '')}
            domain={url}
            getPayload={() => ({
              url,
              all: Object.fromEntries(
                Object.entries(allResults || {})
                  .filter(([id, v]: [string, any]) => v?.data && !RUN_ALL_SKIP.includes(id as ToolId))
                  .map(([id, v]: [string, any]) => [id, v.data])
              ),
            })}
          />
          {/* Živý analyzátor právě běžícího nástroje */}
          {runningAll && currentTool && (
            <div className="mb-4">
              <LiveAnalyzer toolId={currentTool} toolName={TOOLS.find((x) => x.id === currentTool)?.name || currentTool} compact />
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {TOOLS.filter((tool) => !RUN_ALL_SKIP.includes(tool.id)).map(({ id, name }) => {
              const r = allResults[id]
              const isCurrent = runningAll && currentTool === id
              return (
                <button
                  key={id}
                  onClick={() => { if (r?.data) { setActiveTool(id); setResult(r.data); scrollToResult() } }}
                  className={clsx(
                    'flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium border transition',
                    isCurrent ? 'bg-primary-50 border-primary-300 text-primary-700 ring-2 ring-primary-200 animate-pulse' :
                    r?.status === 'done' ? 'bg-green-50 border-green-200 text-green-700 cursor-pointer hover:bg-green-100' :
                    r?.status === 'error' ? 'bg-red-50 border-red-200 text-red-700' :
                    !r ? 'bg-gray-50 border-gray-200 text-gray-400' :
                    'bg-blue-50 border-blue-200 text-blue-700'
                  )}
                >
                  {isCurrent ? <Loader2 className="w-4 h-4 animate-spin text-primary-500" /> :
                   !r ? <span className="w-4 h-4 shrink-0 rounded-full border-2 border-gray-200 inline-block" /> :
                   r.status === 'done' ? <CheckCircle className="w-4 h-4" /> :
                   <XCircle className="w-4 h-4" />}
                  {name}
                  {r?.data?.score != null && (
                    <span className={clsx('ml-auto text-xs font-bold',
                      r.data.score >= 80 ? 'text-green-600' : r.data.score >= 50 ? 'text-yellow-600' : 'text-red-600'
                    )}>{r.data.score}</span>
                  )}
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* Pokročilé nástroje (vyžadují další vstup) — karty pod „Spustit vše" */}
      {allResults && (
        <div className="mb-6">
          <p className="text-sm font-semibold text-gray-700">{t('tools.advanced_title')}</p>
          <p className="text-xs text-gray-400 mb-3">{t('tools.advanced_desc')}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {(['compare', 'bulk'] as const).map((tid) => {
              const tool = TOOLS.find((tt) => tt.id === tid)!
              const Icon = tool.icon
              return (
                <button
                  key={tid}
                  onClick={() => setExtra(tid)}
                  className="flex items-center gap-3 text-left bg-white border border-gray-200 rounded-2xl p-4 hover:border-primary-300 hover:shadow-md transition group"
                >
                  <div className="w-11 h-11 rounded-xl bg-primary-50 flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5 text-primary-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-gray-900">{tool.name}</p>
                    <p className="text-xs text-gray-400 truncate">{tool.desc}</p>
                  </div>
                  <span className="text-xs font-semibold text-primary-600 shrink-0 flex items-center gap-1">
                    {t('tools.fill_run')} <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition" />
                  </span>
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* Výsledek nástroje — hned pod kartami, se slide animací */}
      {runTool.isError && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 animate-slide-up">
          <p className="text-red-700">{
            (runTool.error as any)?.code === 'ECONNABORTED'
              ? t('tools.timeout')
              : (runTool.error as any)?.response?.data?.message || t('tools.analysis_error')
          }</p>
        </div>
      )}

      {runTool.isPending && !runningAll && (
        <LiveAnalyzer toolId={activeTool} toolName={TOOLS.find((x) => x.id === activeTool)?.name || activeTool} />
      )}

      {result && !runTool.isPending && (
        <div key={activeTool} ref={resultRef} className="space-y-4 mb-6 animate-slide-up scroll-mt-20">
          <div className={clsx('flex gap-2 items-center', result?.cached ? 'justify-between' : 'justify-end')}>
            {result?.cached && (
              <span className="inline-flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                <Database className="w-3.5 h-3.5 shrink-0" />
                {!isAuthenticated
                  ? t('tools.cached_note_anon', { defaultValue: 'Uložené měření z cache (pro nepřihlášené platí 1 hodinu).' })
                  : result.cached_ttl_min
                    ? t('tools.cached_note_user', { min: result.cached_ttl_min, defaultValue: 'Výsledky se u tvého plánu cachují {{min}} min (pak čerstvé).' })
                    : t('tools.cached_note', { defaultValue: 'Výsledek z cache (uložené měření)' })}
                {!isAuthenticated && (
                  <Link to="/register" className="font-semibold text-primary-600 hover:text-primary-700 underline decoration-dotted">
                    {t('tools.cached_register', { defaultValue: 'Registrujte se pro vždy čerstvé výsledky' })}
                  </Link>
                )}
                {!allResults && url && (
                  <button
                    onClick={() => runTool.mutate({ tool: activeTool, url, refresh: true })}
                    className="ml-1 font-semibold text-primary-600 hover:text-primary-700 underline decoration-dotted"
                  >
                    {t('tools.remeasure', { defaultValue: 'Přeměřit' })}
                  </button>
                )}
              </span>
            )}
            <div className="flex gap-2">
            {!allResults && (
              <button
                onClick={() => setShareOpen(true)}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-600 dark:text-gray-300 hover:text-primary-700 bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10 px-3 py-1.5 rounded-lg transition"
              >
                <Share2 className="w-4 h-4" /> {t('snapshot.share_btn', { defaultValue: 'Sdílet' })}
              </button>
            )}
            {hasKb(TOOL_KB[activeTool]) && (
              <button
                onClick={() => {
                  const ids = (allResults ? Object.keys(allResults) : [activeTool])
                    .map((id) => TOOL_KB[id]).filter((x) => x && hasKb(x))
                  openKb(TOOL_KB[activeTool], ids)
                }}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 hover:text-primary-700 bg-primary-50 hover:bg-primary-100 px-3 py-1.5 rounded-lg transition"
              >
                <BookOpen className="w-4 h-4" /> {t('kb.learn_more')}
              </button>
            )}
            </div>
          </div>
          {!allResults && (
            <ShareSnapshotModal
              open={shareOpen}
              onClose={() => setShareOpen(false)}
              type="tool"
              title={(TOOLS.find((x) => x.id === activeTool)?.name || activeTool) + (url ? ' — ' + url : '')}
              domain={url}
              getPayload={() => ({ tool: activeTool, toolName: TOOLS.find((x) => x.id === activeTool)?.name || activeTool, url, result })}
            />
          )}
          <ToolResultView tool={activeTool} data={result} url={url} />
          {!result?.error && <ToolRating tool={activeTool} className="mt-6" />}
        </div>
      )}

      {/* Tool selector */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-6">
        {TOOLS.map(({ id, name, icon: Icon }) => (
          <button
            key={id}
            onClick={() => { setActiveTool(id); setResult(null) }}
            className={clsx(
              'flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium transition border',
              activeTool === id
                ? 'bg-primary-50 border-primary-300 text-primary-700'
                : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
            )}
          >
            <Icon className="w-4 h-4 shrink-0" />
            <span className="flex flex-col items-start min-w-0 leading-tight">
              <span className="truncate">{name}</span>
              <RatingBadge summary={ratingsAll?.[id]} className="mt-0.5" />
            </span>
          </button>
        ))}
      </div>

      {/* AI nástroje (BYOK) — vlastní panel místo URL formuláře */}
      {isAiTool(activeTool) && (
        <>
          <p className="text-sm text-gray-500 mb-3">{TOOLS.find((t) => t.id === activeTool)?.desc}</p>
          <AiToolPanel toolId={activeTool} />
        </>
      )}

      {/* URL input */}
      {!isAiTool(activeTool) && (
      <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
        <p className="text-sm text-gray-500 mb-3">
          {TOOLS.find((t) => t.id === activeTool)?.desc}
        </p>
        {activeTool === 'bulk' ? (
          <div className="space-y-3">
            {!isAuthenticated && (
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border border-primary-100 dark:border-white/10 bg-gradient-to-r from-primary-50 to-accent-50 dark:from-primary-500/10 dark:to-accent-500/10 p-4">
                <Sparkles className="w-5 h-5 text-primary-600 dark:text-primary-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">{t('tools.bulk_guest_title', { defaultValue: 'Spusťte hromadnou kontrolu naplno' })}</p>
                  <p className="text-xs text-gray-600 dark:text-gray-300 mt-0.5">{t('tools.bulk_guest_desc', { defaultValue: 'Vytvořte si účet zdarma a kontrolujte desítky URL najednou, s uloženou historií a exportem výsledků.' })}</p>
                </div>
                <Link to="/register" className="shrink-0 inline-flex items-center justify-center gap-1.5 bg-gradient-to-r from-primary-600 to-primary-500 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:shadow-lg transition">
                  {t('tools.bulk_guest_cta', { defaultValue: 'Registrovat zdarma' })} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            )}
            <textarea
              value={bulkUrls}
              onChange={(e) => setBulkUrls(e.target.value)}
              placeholder={`${t('tres.urls_placeholder')}\nexample.com\nexample.com/page1\nexample.com/page2`}
              rows={5}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 font-mono text-sm"
            />
            <button
              type="submit"
              disabled={runTool.isPending || !bulkUrls.trim()}
              className="bg-primary-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-primary-700 transition disabled:opacity-50 flex items-center gap-2"
            >
              {runTool.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              {t('tools.check_urls_count', { count: bulkUrls.split(/[\n,]+/).filter(u => u.trim()).length, defaultValue: 'Zkontrolovat ({{count}} URL)' })}
            </button>
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row gap-3">
            <div className={clsx('flex-1 min-w-0 flex gap-3', activeTool === 'compare' && 'flex-col sm:flex-row')}>
              <input
                type="text"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder={activeTool === 'compare' ? t('tres.web_a_placeholder') : t('crawl.domain_placeholder')}
                className="flex-1 min-w-0 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              {activeTool === 'compare' && (
                <input
                  type="text"
                  value={url2}
                  onChange={(e) => setUrl2(e.target.value)}
                  placeholder={t('tres.web_b_placeholder')}
                  className="flex-1 min-w-0 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              )}
            </div>
            <button
              type="submit"
              disabled={runTool.isPending || !url.trim() || (activeTool === 'compare' && !url2.trim())}
              className="bg-primary-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-primary-700 transition disabled:opacity-50 flex items-center justify-center gap-2 w-full sm:w-auto sm:self-end shrink-0"
            >
              {runTool.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              {activeTool === 'compare' ? t('tools.compare_btn') : t('tools.analyze')}
            </button>
          </div>
        )}
      </form>
      )}

      {/* Popup: vyplnit vstup pro pokročilý nástroj (compare / bulk) */}
      {extra && (() => {
        const tool = TOOLS.find((tt) => tt.id === extra)!
        return (
          // Backdrop wrapper: click-outside-to-close (modal has a visible close button)
          // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" onClick={() => setExtra(null)}>
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
            {/* Modal — onClick only stops backdrop close from firing inside it; not an interactive control */}
            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
            <div ref={extraModalRef} role="dialog" aria-modal="true" className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden animate-fade-in" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                <h3 className="font-bold text-gray-900 text-lg flex items-center gap-2">
                  <tool.icon className="w-5 h-5 text-primary-500" />
                  {tool.name}
                </h3>
                <button onClick={() => setExtra(null)} className="text-gray-400 hover:text-gray-700 transition"><X className="w-5 h-5" /></button>
              </div>
              <form onSubmit={(e) => { e.preventDefault(); runExtra() }} className="px-6 py-5">
                {extra === 'compare' ? (
                  <>
                    {url && <p className="text-xs text-gray-500 mb-2">Web A: <span className="font-mono text-gray-700">{url}</span></p>}
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('tools.compare_label')}</label>
                    <input type="text" value={extraUrl2} onChange={(e) => setExtraUrl2(e.target.value)}
                      // eslint-disable-next-line jsx-a11y/no-autofocus -- primary input of the compare modal, focusing it speeds up the flow
                      autoFocus
                      placeholder={t('tres.web_b_placeholder')}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
                  </>
                ) : (
                  <>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('tools.bulk_label')}</label>
                    <textarea value={extraUrls} onChange={(e) => setExtraUrls(e.target.value)} rows={6}
                      // eslint-disable-next-line jsx-a11y/no-autofocus -- primary input of the bulk modal, focusing it speeds up the flow
                      autoFocus
                      placeholder={t('tres.urls_placeholder')}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none" />
                  </>
                )}
                <div className="flex gap-2 mt-4">
                  <button type="button" onClick={() => setExtra(null)}
                    className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-semibold hover:bg-gray-50 transition">
                    {t('common.cancel')}
                  </button>
                  <button type="submit" disabled={extra === 'compare' ? !extraUrl2.trim() : !extraUrls.trim()}
                    className="flex-1 py-2.5 rounded-xl bg-primary-600 hover:bg-primary-700 disabled:opacity-40 text-white text-sm font-semibold transition flex items-center justify-center gap-1.5">
                    {t('tools.run_now')} <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </form>
            </div>
          </div>
        )
      })()}

      {/* Průvodci nástroji — interní prolinkování + SEO landing pages */}
      <div className="mt-12 border-t border-gray-100 dark:border-white/10 pt-8">
        <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
          {t('toolpage.guides_title', { defaultValue: 'Průvodci jednotlivými nástroji' })}
        </h2>
        <p className="text-sm text-gray-500 mb-4">
          {t('toolpage.guides_desc', { defaultValue: 'Podrobné vysvětlení, co každý nástroj kontroluje a proč na tom v SEO záleží.' })}
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1">
          {TOOL_PAGES.map((p) => (
            <Link key={p.slug} to={toolPath(p.slug, i18n.language)}
              className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 hover:text-primary-600 py-1.5 transition">
              <ArrowRight className="w-3.5 h-3.5 text-primary-400 shrink-0" />{toolH1(p.slug, i18n.language)}
            </Link>
          ))}
        </div>
      </div>

      {/* Veřejná statistika používání nástrojů + poslední kontroly */}
      <PublicToolUsageCard className="mt-10" />

      <SubscriptionGate open={gateOpen} onClose={() => setGateOpen(false)} feature={t('analysis.export_pdf')} />

      {/* Vyčerpaný denní limit pro nepřihlášené → nabídka registrace */}
      {limitReached && (
        // Backdrop wrapper: click-outside-to-close (modal has a visible close button)
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" onClick={() => setLimitReached(null)}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          {/* Modal — onClick only stops backdrop close from firing inside it; not an interactive control */}
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
          <div ref={limitModalRef} role="dialog" aria-modal="true" className="relative w-full max-w-md bg-white dark:bg-zinc-900 rounded-2xl shadow-2xl overflow-hidden animate-fade-in" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setLimitReached(null)} className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 p-1"><X className="w-5 h-5" /></button>
            <div className="bg-gradient-to-br from-primary-600 to-cyan-500 px-6 py-7 text-center">
              <div className="w-14 h-14 mx-auto rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center mb-3">
                <Wrench className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white">{t('tres.limit_title', { defaultValue: 'Pokračujte zdarma' })}</h3>
            </div>
            <div className="px-6 py-6 text-center">
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-5">{limitReached}</p>
              <div className="flex flex-col gap-2">
                <Link to="/register" onClick={() => setLimitReached(null)}
                  className="w-full bg-gradient-to-r from-primary-600 to-primary-500 text-white px-5 py-3 rounded-xl font-semibold hover:shadow-lg hover:shadow-primary-500/25 transition">
                  {t('tres.limit_register', { defaultValue: 'Zaregistrovat se zdarma' })}
                </Link>
                <Link to="/login" onClick={() => setLimitReached(null)}
                  className="w-full text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 py-2">
                  {t('tres.limit_login', { defaultValue: 'Už mám účet, přihlásit se' })}
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Jednorázová „nejsi robot?" brána pro anonymy před prvním spuštěním nástroje */}
      {humanGate && (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" onClick={() => closeHumanGate(false)}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
          <div ref={humanGateRef} role="dialog" aria-modal="true" className="relative w-full max-w-sm bg-white dark:bg-zinc-900 rounded-2xl shadow-2xl overflow-hidden animate-fade-in" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => closeHumanGate(false)} className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 p-1"><X className="w-5 h-5" /></button>
            <div className="px-6 py-7 text-center">
              <div className="w-12 h-12 mx-auto rounded-2xl bg-primary-100 dark:bg-primary-900/40 flex items-center justify-center mb-3">
                <ShieldCheck className="w-6 h-6 text-primary-600 dark:text-primary-400" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">{t('tools.human_title', { defaultValue: 'Rychlé ověření' })}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{t('tools.human_desc', { defaultValue: 'Potvrďte, že nejste robot, stačí jednou, pak můžete nástroje používat bez přerušení.' })}</p>
              <Turnstile onVerify={(tok) => { if (tok) submitHuman(tok) }} theme="auto" className="flex justify-center min-h-[70px]" />
              {humanBusy && <div className="flex items-center justify-center gap-2 text-sm text-gray-500 mt-3"><Loader2 className="w-4 h-4 animate-spin" />{t('tools.human_verifying', { defaultValue: 'Ověřuji…' })}</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// --- Result Components ---

function StatusBadge({ pass }: { pass: boolean }) {
  return pass ? (
    <CheckCircle className="w-5 h-5 text-green-500" />
  ) : (
    <XCircle className="w-5 h-5 text-red-500" />
  )
}

function ScoreBadge({ score }: { score: number }) {
  return (
    <span className={clsx(
      'text-3xl font-bold',
      score >= 80 ? 'text-green-600' : score >= 50 ? 'text-yellow-600' : 'text-red-600'
    )}>
      {score}
    </span>
  )
}

function IssuesList({ issues }: { issues: string[] }) {
  if (!issues?.length) return null
  return (
    <div className="mt-4 space-y-2">
      {issues.map((issue, i) => (
        <div key={i} className="flex items-start gap-2 text-sm">
          <AlertTriangle className="w-4 h-4 text-yellow-500 mt-0.5 shrink-0" />
          <span className="text-gray-700">{issue}</span>
        </div>
      ))}
    </div>
  )
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
      <h3 className="font-semibold text-gray-900 mb-4">{title}</h3>
      {children}
    </div>
  )
}

// Živý analyzátor — odanimuje konkrétní kroky nástroje, ať uživatel vidí, co se právě děje
function LiveAnalyzer({ toolId, toolName, compact }: { toolId: ToolId; toolName: string; compact?: boolean }) {
  const { t } = useTranslation()
  const raw = t(`tool_steps.${toolId}`, { returnObjects: true }) as unknown
  const steps = Array.isArray(raw) ? (raw as string[]) : ['Analyzuji…']
  const [idx, setIdx] = useState(0)
  useEffect(() => {
    setIdx(0)
    const iv = setInterval(() => setIdx((i) => Math.min(i + 1, steps.length - 1)), 750)
    return () => clearInterval(iv)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toolId])
  return (
    <div className={clsx('bg-gradient-to-br from-primary-50 to-cyan-50 border border-primary-100 rounded-2xl', compact ? 'p-4' : 'p-5 mb-6')}>
      <div className="flex items-center gap-2 mb-3">
        <span className="relative flex h-2.5 w-2.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-primary-500" />
        </span>
        <span className="text-sm font-semibold text-gray-800">Analyzuji: {toolName}</span>
      </div>
      <div className="space-y-1.5">
        {steps.map((s, i) => (
          <div key={i} className={clsx('flex items-center gap-2 text-sm transition-all duration-300',
            i < idx ? 'text-green-600' : i === idx ? 'text-gray-800 font-medium' : 'text-gray-300')}>
            {i < idx ? <CheckCircle className="w-4 h-4 shrink-0" /> :
             i === idx ? <Loader2 className="w-4 h-4 shrink-0 animate-spin text-primary-500" /> :
             <span className="w-4 h-4 shrink-0 rounded-full border-2 border-gray-200 inline-block" />}
            {s}
          </div>
        ))}
      </div>
    </div>
  )
}

function RobotsResult({ data }: { data: any }) {
  const { t } = useTranslation()
  const [showRaw, setShowRaw] = useState(false)
  return (
    <>
      <Card title="Robots.txt">
        <div className="flex items-center gap-3 mb-4">
          <StatusBadge pass={data.exists} />
          <span className={data.exists ? 'text-green-700' : 'text-red-700'}>
            {data.exists ? t('tres.robots_found') : t('tres.robots_not_found')}
          </span>
        </div>
        {data.bot_access && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            {Object.entries(data.bot_access).map(([bot, status]) => (
              <div key={bot} className={clsx(
                'px-3 py-2 rounded-lg text-xs font-medium text-center',
                status === 'allowed' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
              )}>
                {bot}: {status === 'allowed' ? t('tres.allowed') : t('tres.blocked')}
              </div>
            ))}
          </div>
        )}
        {data.sitemaps?.length > 0 && (
          <div className="mb-4">
            <p className="text-sm font-medium text-gray-700 mb-1">{t('tres.sitemaps_label')}</p>
            {data.sitemaps.map((s: string, i: number) => (
              <p key={i} className="text-sm text-primary-600 truncate">{s}</p>
            ))}
          </div>
        )}
        <IssuesList issues={data.issues} />
        {data.content && (
          <button onClick={() => setShowRaw(!showRaw)} className="text-sm text-gray-500 mt-4 flex items-center gap-1">
            {showRaw ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            {showRaw ? t('tres.hide_content') : t('tres.show_content')}
          </button>
        )}
        {showRaw && (
          <pre className="mt-2 bg-gray-50 dark:bg-white/5 p-4 rounded-lg text-xs max-h-64 overflow-y-auto whitespace-pre-wrap break-words max-w-full text-gray-600 dark:text-gray-300">{data.content}</pre>
        )}
      </Card>
    </>
  )
}

function SitemapResult({ data }: { data: any }) {
  const { t } = useTranslation()
  return (
    <Card title={`Sitemap — ${data.type === 'index' ? 'Index' : 'URL list'}`}>
      <p className="text-sm text-gray-600 mb-4">
        {t('card.total')} <strong>{data.total_urls}</strong> {data.type === 'index' ? t('tres.sub_sitemaps') : 'URL'}
      </p>
      {data.child_sitemaps?.length > 0 && (
        <div className="space-y-1 mb-4">
          {data.child_sitemaps.map((s: string, i: number) => (
            <p key={i} className="text-sm text-primary-600 truncate">{s}</p>
          ))}
        </div>
      )}
      {data.entries?.length > 0 && (
        <div className="overflow-x-auto max-h-96">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="pb-2 font-medium">URL</th>
                <th className="pb-2 font-medium">Lastmod</th>
                <th className="pb-2 font-medium">Priority</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {data.entries.map((e: any, i: number) => (
                <tr key={i}>
                  <td className="py-1.5 max-w-md truncate text-primary-600">{e.loc}</td>
                  <td className="py-1.5 text-gray-500">{e.lastmod || '–'}</td>
                  <td className="py-1.5 text-gray-500">{e.priority || '–'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data.truncated && <p className="text-xs text-gray-400 mt-2">{t('tres.showing_max', { max: 200, total: data.total_urls })}</p>}
      <IssuesList issues={data.issues} />
    </Card>
  )
}

function SchemaResult({ data }: { data: any }) {
  const { t } = useTranslation()
  return (
    <Card title={t('checks.structured_data')}>
      <p className="text-sm text-gray-600 mb-4">{t('tres.found')} <strong>{data.total_schemas}</strong> {t('tres.schemas')}</p>
      {data.json_ld?.map((s: any, i: number) => (
        <div key={i} className="mb-3 p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <StatusBadge pass={s.valid_json} />
            <span className="font-medium text-sm">{s.type} (JSON-LD)</span>
          </div>
          {s.valid_json && (
            <pre className="text-xs text-gray-600 overflow-x-auto max-h-32">{JSON.stringify(s.data, null, 2)}</pre>
          )}
          {!s.valid_json && <p className="text-xs text-red-600">{s.error}</p>}
        </div>
      ))}
      {data.microdata?.length > 0 && (
        <p className="text-sm text-gray-600">Microdata: {data.microdata.join(', ')}</p>
      )}
      <IssuesList issues={data.issues} />
      {data.recommendations?.length > 0 && (
        <div className="mt-4">
          <p className="text-sm font-medium text-gray-700 mb-2">{t('common.recommendation')}:</p>
          {data.recommendations.map((r: string, i: number) => (
            <p key={i} className="text-sm text-blue-600 flex items-center gap-1">
              <span className="text-blue-400">+</span> {r}
            </p>
          ))}
        </div>
      )}
    </Card>
  )
}

function SerpResult({ data }: { data: any }) {
  const { t } = useTranslation()
  return (
    <Card title="SERP Preview">
      {/* Google-like preview */}
      <div className="bg-white border border-gray-200 rounded-lg p-5 mb-6 max-w-2xl">
        <div className="flex items-center gap-2 mb-1">
          {data.favicon && <img src={data.favicon} alt="" className="w-4 h-4" onError={(e) => (e.currentTarget.style.display = 'none')} />}
          <span className="text-xs text-gray-600">{data.preview?.url}</span>
        </div>
        <h3 className="text-xl text-blue-800 hover:underline cursor-pointer mb-1 leading-snug">
          {data.preview?.title || <span className="text-gray-400 italic">{t('tres.missing_title')}</span>}
        </h3>
        <p className="text-sm text-gray-600 leading-relaxed">
          {data.preview?.description || <span className="text-gray-400 italic">{t('tres.missing_meta')}</span>}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4 text-sm mb-4">
        <div>
          <span className="text-gray-500">Title:</span>
          <span className={clsx('ml-2 font-medium', data.title_length > 60 ? 'text-yellow-600' : data.title_length === 0 ? 'text-red-600' : 'text-green-600')}>
            {data.title_length} {t('crawl.chars_full')}
          </span>
        </div>
        <div>
          <span className="text-gray-500">Description:</span>
          <span className={clsx('ml-2 font-medium', data.description_length > 160 ? 'text-yellow-600' : data.description_length === 0 ? 'text-red-600' : 'text-green-600')}>
            {data.description_length} {t('crawl.chars_full')}
          </span>
        </div>
      </div>

      {data.open_graph && Object.keys(data.open_graph).length > 0 && (
        <div className="mt-4">
          <p className="text-sm font-medium text-gray-700 mb-2">Open Graph:</p>
          <div className="grid grid-cols-2 gap-1 text-xs">
            {Object.entries(data.open_graph).map(([key, val]) => (
              <div key={key}><span className="text-gray-500">og:{key}:</span> <span className="text-gray-700">{val as string}</span></div>
            ))}
          </div>
        </div>
      )}
      <IssuesList issues={data.issues?.map((i: any) => i.message)} />
    </Card>
  )
}

function HreflangResult({ data }: { data: any }) {
  const { t } = useTranslation()
  return (
    <Card title={t('tres.hreflang_title')}>
      <p className="text-sm text-gray-600 mb-4">{t('tres.found')} <strong>{data.total}</strong> {t('tres.hreflang_tags')}</p>
      {data.hreflangs?.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="pb-2 font-medium">{t('tres.lang_col')}</th>
                <th className="pb-2 font-medium">URL</th>
                <th className="pb-2 font-medium">{t('tres.source_col')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {data.hreflangs.map((h: any, i: number) => (
                <tr key={i}>
                  <td className="py-2 font-mono text-sm">{h.lang}</td>
                  <td className="py-2 text-primary-600 truncate max-w-sm">{h.url}</td>
                  <td className="py-2 text-xs text-gray-400">{h.source}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-gray-400 text-sm">{t('tres.no_hreflang')}</p>
      )}
      <div className="flex gap-3 mt-4">
        <span className={clsx('text-xs px-2 py-1 rounded-full', data.has_x_default ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700')}>
          x-default: {data.has_x_default ? t('common.yes') : t('tres.missing')}
        </span>
        <span className={clsx('text-xs px-2 py-1 rounded-full', data.self_referencing ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700')}>
          Self-ref: {data.self_referencing ? t('common.yes') : t('tres.missing')}
        </span>
      </div>
      <IssuesList issues={data.issues} />
    </Card>
  )
}

function MobileResult({ data }: { data: any }) {
  const { t } = useTranslation()
  return (
    <Card title="Mobile-Friendly Test">
      <div className="flex items-center gap-4 mb-6">
        <ScoreBadge score={data.score} />
        <span className={clsx('text-lg font-medium', data.is_mobile_friendly ? 'text-green-600' : 'text-red-600')}>
          {data.is_mobile_friendly ? t('tres.mobile_friendly') : t('tres.not_mobile')}
        </span>
      </div>
      <div className="space-y-3">
        {Object.entries(data.checks || {}).map(([key, val]: [string, any]) => (
          <div key={key} className="flex items-center gap-3 text-sm">
            {val.pass !== undefined && <StatusBadge pass={val.pass} />}
            <span className="text-gray-700">{val.message || key}</span>
          </div>
        ))}
      </div>
      <IssuesList issues={data.issues} />
    </Card>
  )
}

function SecurityResult({ data }: { data: any }) {
  const { t } = useTranslation()
  return (
    <Card title={t('tres.security_headers')}>
      <div className="flex items-center gap-4 mb-6">
        <ScoreBadge score={data.score} />
        <span className="text-gray-500">/ 100</span>
      </div>
      <div className="space-y-3">
        {Object.entries(data.headers || {}).map(([key, val]: [string, any]) => (
          <div key={key} className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
            <div className="flex items-center gap-3">
              <StatusBadge pass={val.present} />
              <div>
                <p className="font-medium text-sm text-gray-900">{val.name}</p>
                <p className="text-xs text-gray-500">{val.description}</p>
              </div>
            </div>
            {val.present && (
              <code className="text-xs text-gray-600 max-w-xs truncate">{val.value}</code>
            )}
          </div>
        ))}
      </div>
    </Card>
  )
}

function PageSpeedResult({ data }: { data: any }) {
  const { t } = useTranslation()
  const { open: openKb } = useKnowledge()
  const oppIds: string[] = (data.opportunities || []).map((o: any) => o.id).filter(hasKb)
  return (
    <>
      <Card title={`PageSpeed — ${data.strategy === 'mobile' ? 'Mobil' : 'Desktop'}`}>
        <div className="flex items-center gap-6 mb-6">
          <div className="text-center">
            <ScoreBadge score={data.score} />
            <p className="text-xs text-gray-500 mt-1">{t('tres.performance')}</p>
          </div>
          <div className="flex-1 grid grid-cols-3 gap-4">
            {data.metrics?.slice(0, 6).map((m: any) => (
              <div key={m.id} className="text-center">
                <p className={clsx('text-lg font-semibold', m.score >= 80 ? 'text-green-600' : m.score >= 50 ? 'text-yellow-600' : 'text-red-600')}>
                  {m.value}
                </p>
                <p className="text-xs text-gray-500">{m.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Resource details */}
        {data.details && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-4 border-t border-gray-100">
            {[
              { label: t('tres.css_files'), value: data.details.css_files, warn: data.details.css_files > 5 },
              { label: t('tres.js_files'), value: data.details.js_files, warn: data.details.js_files > 8 },
              { label: t('tres.images'), value: data.details.images, warn: false },
              { label: t('tres.dom_elements'), value: data.details.dom_elements, warn: data.details.dom_elements > 1500 },
              { label: t('tres.blocking_css'), value: data.details.render_blocking_css, warn: data.details.render_blocking_css > 2 },
              { label: t('tres.blocking_js'), value: data.details.render_blocking_js, warn: data.details.render_blocking_js > 0 },
              { label: t('tres.compression'), value: data.details.compression, warn: data.details.compression === 'žádná' },
              { label: t('tres.cache'), value: data.details.has_caching ? t('common.yes') : t('common.no'), warn: !data.details.has_caching },
            ].map(({ label, value, warn }) => (
              <div key={label} className={clsx('px-3 py-2 rounded-lg text-center text-xs', warn ? 'bg-yellow-50 text-yellow-800' : 'bg-gray-50 text-gray-700')}>
                <p className="font-semibold text-sm">{value}</p>
                <p>{label}</p>
              </div>
            ))}
          </div>
        )}
      </Card>

      {data.opportunities?.length > 0 && (
        <Card title={t('tres.improvements')}>
          <div className="space-y-3">
            {data.opportunities.map((o: any) => (
              <div key={o.id} className="p-3 bg-yellow-50 rounded-lg">
                <p className="font-medium text-sm text-gray-900">{o.title}</p>
                <p className="text-xs text-gray-600 mt-0.5">{o.description}</p>
                <div className="flex items-center gap-3 mt-1">
                  {o.savings != null && <p className="text-xs text-yellow-700">{t('tres.potential_savings')}: ~{Math.round(o.savings)} KB</p>}
                  {hasKb(o.id) && (
                    <button
                      onClick={() => openKb(o.id, oppIds)}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-primary-600 hover:text-primary-700 hover:gap-1.5 transition-all"
                    >
                      <BookOpen className="w-3.5 h-3.5" /> {t('kb.learn_more')}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {data.diagnostics?.length > 0 && (
        <Card title={t('tres.diagnostics')}>
          <div className="space-y-2">
            {data.diagnostics.map((d: any) => (
              <div key={d.id} className="flex items-start gap-2 text-sm">
                <AlertTriangle className="w-4 h-4 text-orange-500 mt-0.5 shrink-0" />
                <div>
                  <p className="text-gray-900">{d.title}</p>
                  {d.display_value && <p className="text-xs text-gray-500">{d.display_value}</p>}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </>
  )
}

function LhScoreRing({ score, label }: { score: number | null; label: string }) {
  const s = score ?? 0
  const color = s >= 90 ? '#16a34a' : s >= 50 ? '#ca8a04' : '#dc2626'
  const r = 28
  const circ = 2 * Math.PI * r
  const offset = circ - (s / 100) * circ
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative w-20 h-20">
        <svg className="w-20 h-20 -rotate-90" viewBox="0 0 64 64">
          <circle cx="32" cy="32" r={r} fill="none" stroke="#e5e7eb" strokeWidth="5" />
          <circle cx="32" cy="32" r={r} fill="none" stroke={color} strokeWidth="5"
            strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round" />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center text-xl font-bold" style={{ color }}>
          {score == null ? '–' : score}
        </span>
      </div>
      <p className="text-xs text-center text-gray-600 max-w-[5rem] leading-tight">{label}</p>
    </div>
  )
}

function LighthouseResult({ data, url }: { data: any; url?: string }) {
  const { t } = useTranslation()
  // Cache výsledků per strategie + možnost přepínat Mobil/Desktop (audit se doběhne lazy)
  const initStrategy: 'mobile' | 'desktop' = data?.strategy === 'desktop' ? 'desktop' : 'mobile'
  const [strategy, setStrategy] = useState<'mobile' | 'desktop'>(initStrategy)
  const [cache, setCache] = useState<Record<string, any>>({})
  // Která strategie se právě načítá (ne globální boolean) — jinak by spinner z běžícího
  // desktop auditu „zakryl" už hotový mobil po přepnutí zpět a vypadalo by to jako další běh.
  const [loadingStrategy, setLoadingStrategy] = useState<'mobile' | 'desktop' | null>(null)
  const [error, setError] = useState<string | null>(null)
  // Guard proti duplicitnímu volání téže strategie (rychlé přepínání tam a zpět).
  const inFlight = useRef<Set<string>>(new Set())

  // Naseeduj cache výsledkem z rodiče (a resetuj při novém běhu / změně URL)
  useEffect(() => {
    if (data && !data.error) {
      setCache({ [data.strategy === 'desktop' ? 'desktop' : 'mobile']: data })
      setStrategy(data.strategy === 'desktop' ? 'desktop' : 'mobile')
    } else {
      setCache({})
    }
    setError(null)
  }, [data])

  // Audit dané strategie. inFlight + cache hlídá, že každá strategie poběží max 1×.
  // Výsledek se uloží do cache → přepnutí mezi Mobil/Desktop je pak okamžité.
  const fetchStrategy = async (s: 'mobile' | 'desktop') => {
    if (!url || cache[s] || inFlight.current.has(s)) return
    inFlight.current.add(s)
    setLoadingStrategy(s)
    setError(null)
    try {
      const { data: res } = await api.post('/tools/lighthouse', { url, strategy: s })
      if (res?.error) setError(res.message || 'Chyba')
      else setCache((c) => ({ ...c, [s]: res }))
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Audit se nezdařil.')
    } finally {
      inFlight.current.delete(s)
      setLoadingStrategy((cur) => (cur === s ? null : cur))
    }
  }

  // Přednačti druhou strategii na pozadí, jakmile dorazí první výsledek — než uživatel
  // přepne na druhou záložku, je už hotová (nebo aspoň běží). Spinner se na aktivní
  // záložce neukáže (běží neviditelně), guard zajistí jediný běh každé strategie.
  useEffect(() => {
    if (!url || !data || data.error) return
    const other: 'mobile' | 'desktop' = data.strategy === 'desktop' ? 'mobile' : 'desktop'
    const id = setTimeout(() => { fetchStrategy(other) }, 600)
    return () => clearTimeout(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, url])

  const switchTo = (s: 'mobile' | 'desktop') => {
    setStrategy(s)
    if (!cache[s]) fetchStrategy(s)
  }

  const active = cache[strategy]

  const tabs = (
    <div className="inline-flex rounded-xl border border-gray-200 bg-gray-50 p-1 mb-4">
      {(['mobile', 'desktop'] as const).map((s) => (
        <button
          key={s}
          onClick={() => switchTo(s)}
          className={clsx(
            'flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-medium transition',
            strategy === s ? 'bg-white shadow-sm text-primary-700' : 'text-gray-500 hover:text-gray-700'
          )}
        >
          {s === 'mobile' ? <Smartphone className="w-4 h-4" /> : <BarChart3 className="w-4 h-4" />}
          {s === 'mobile' ? 'Mobil' : 'Desktop'}
        </button>
      ))}
    </div>
  )

  if (data?.error) {
    return <Card title="Lighthouse">{tabs}<p className="text-sm text-red-600">{data.message}</p></Card>
  }
  // Cache zobraz okamžitě (i když na pozadí dobíhá jiná strategie). Spinner jen tehdy,
  // když právě zobrazená strategie nemá data a opravdu se načítá.
  if (!active) {
    const isLoading = loadingStrategy === strategy
    return (
      <Card title="Lighthouse">
        {tabs}
        <div className="flex items-center gap-2 text-sm text-gray-500 py-6 justify-center">
          {isLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Spouštím {strategy === 'mobile' ? 'mobilní' : 'desktop'} audit…</> : (error || 'Bez dat')}
        </div>
        {error && !isLoading && <p className="text-sm text-red-600 text-center">{error}</p>}
      </Card>
    )
  }

  const d = active
  const cats = d.categories || {}
  const order = ['performance', 'accessibility', 'best-practices', 'seo']
  // Všechny kategorie 100 → ohňostroj 🎉 (jako Lighthouse v DevTools)
  const shownCats = order.filter((k) => cats[k])
  const allPerfect = shownCats.length >= 4 && shownCats.every((k) => Math.round(cats[k].score) === 100)
  return (
    <>
      {allPerfect && <Confetti />}
      <Card title={`Lighthouse — ${strategy === 'desktop' ? 'Desktop' : 'Mobil'}`}>
        {tabs}
        {allPerfect && (
          <div className="mb-5 flex items-center justify-center gap-2 text-sm font-bold text-emerald-600 bg-emerald-50 dark:bg-emerald-500/10 rounded-xl py-2.5 px-4">
            🎉 {t('tools.lh_perfect', { defaultValue: 'Perfektní! Všechny kategorie 100/100.' })}
          </div>
        )}
        <div className="flex flex-wrap items-center justify-around gap-4 mb-6">
          {order.filter((k) => cats[k]).map((k) => (
            <LhScoreRing key={k} score={cats[k].score} label={cats[k].title} />
          ))}
        </div>

        {/* Core Web Vitals */}
        {d.metrics?.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 pt-4 border-t border-gray-100">
            {d.metrics.map((m: any) => (
              <div key={m.id} className="text-center px-3 py-2 rounded-lg bg-gray-50">
                <p className={clsx('text-lg font-semibold', (m.score ?? 0) >= 90 ? 'text-green-600' : (m.score ?? 0) >= 50 ? 'text-yellow-600' : 'text-red-600')}>
                  {m.value || '–'}
                </p>
                <p className="text-xs text-gray-500">{m.title}</p>
              </div>
            ))}
          </div>
        )}
        {/* Náhled stránky + filmstrip načítání */}
        {(d.screenshot || d.filmstrip?.length > 0) && (
          <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap gap-4 items-start">
            {d.screenshot && (
              <div className="shrink-0">
                <img src={d.screenshot} alt="Náhled stránky" className="w-28 rounded-lg border border-gray-200 shadow-sm" />
                <p className="text-[10px] text-gray-400 text-center mt-1">Náhled</p>
              </div>
            )}
            {d.filmstrip?.length > 0 && (
              <div className="flex-1 min-w-0">
                <p className="text-[10px] text-gray-400 mb-1">Průběh načítání</p>
                <div className="flex gap-1.5 overflow-x-auto pb-1">
                  {d.filmstrip.map((f: any, i: number) => (
                    <div key={i} className="shrink-0 text-center">
                      <img src={f.data} alt="" className="h-20 rounded border border-gray-100" />
                      <p className="text-[11px] text-gray-400 mt-0.5">{(f.timing / 1000).toFixed(1)}s</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {d.lighthouse_version && (
          <p className="text-[11px] text-gray-400 mt-4">Lighthouse {d.lighthouse_version}{d.source === 'psi' ? ' · Google PSI' : ''}</p>
        )}
      </Card>

      {d.opportunities?.length > 0 && (
        <Card title={t('tres.improvements')}>
          <div className="space-y-3">
            {d.opportunities.map((o: any) => (
              <div key={o.id} className="p-3 bg-yellow-50 rounded-lg">
                <p className="font-medium text-sm text-gray-900">{o.title}</p>
                {o.description && <p className="text-xs text-gray-600 mt-0.5">{o.description}</p>}
                <div className="flex items-center gap-3 mt-1">
                  {o.savings_ms != null && o.savings_ms > 0 && <p className="text-xs text-yellow-700">{t('tres.potential_savings')}: ~{Math.round(o.savings_ms)} ms</p>}
                  {o.savings_bytes != null && o.savings_bytes > 0 && <p className="text-xs text-yellow-700">~{Math.round(o.savings_bytes / 1024)} KB</p>}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {d.diagnostics?.length > 0 && (
        <Card title={t('tres.diagnostics')}>
          <div className="space-y-2">
            {d.diagnostics.map((diag: any) => (
              <div key={diag.id} className="flex items-start gap-2 text-sm">
                <AlertTriangle className="w-4 h-4 text-orange-500 mt-0.5 shrink-0" />
                <div>
                  <p className="text-gray-900">{diag.title}</p>
                  {diag.display_value && <p className="text-xs text-gray-500">{diag.display_value}</p>}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {d.audits_by_category && <LighthouseAudits abc={d.audits_by_category} cats={cats} />}
    </>
  )
}

// Lighthouse `description` je markdown s odkazem "[text](url)" na dokumentaci.
// Rozdělíme na čistý text + odkazy (vykreslíme je zvlášť jako tlačítka v popupu).
function parseLighthouseDesc(desc: string): { text: string; links: { label: string; url: string }[] } {
  const links: { label: string; url: string }[] = []
  const text = (desc || '')
    .replace(/\[([^\]]*)\]\((https?:\/\/[^)]+)\)/g, (_m, label: string, url: string) => {
      links.push({ label: (label || '').trim(), url })
      return ''
    })
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/ +([.,!?])/g, '$1')
    .trim()
  return { text, links }
}

// Popup s vysvětlením auditu (co/proč/jak) + odkaz na Google/web.dev nápovědu — jako v PSI.
function LighthouseAuditModal({ audit, onClose }: { audit: any; onClose: () => void }) {
  const { text, links } = parseLighthouseDesc(audit.description || '')
  const pct = audit.score == null ? null : Math.round(audit.score * 100)
  const scoreColor = pct == null ? 'text-gray-400' : pct >= 90 ? 'text-green-600' : pct >= 50 ? 'text-yellow-600' : 'text-red-600'
  // Portál do <body> — jinak by `fixed` bylo relativní k animovanému (transform) rodiči
  // z Motion reveal a popup by se necentroval k oknu prohlížeče, ale ke obsahu.
  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" onClick={onClose} role="dialog" aria-modal="true">
      <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-2xl ring-1 ring-black/10 dark:ring-white/10 max-w-lg w-full max-h-[85vh] overflow-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 mb-4">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white pr-2">{audit.title}</h3>
          <button onClick={onClose} aria-label="Zavřít" className="text-gray-400 hover:text-gray-600 shrink-0"><X className="w-5 h-5" /></button>
        </div>
        {(pct != null || audit.display_value) && (
          <div className="flex items-center gap-3 mb-4 text-sm">
            {pct != null && <span className={clsx('font-bold text-base', scoreColor)}>{pct}/100</span>}
            {audit.display_value && <span className="text-gray-500 dark:text-gray-400">{audit.display_value}</span>}
          </div>
        )}
        {text && (
          <>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1.5">Co to znamená a jak to opravit</p>
            <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed whitespace-pre-line">{text}</p>
          </>
        )}
        {links.length > 0 && (
          <div className="mt-5 flex flex-wrap gap-2">
            {links.map((l, i) => (
              <a key={i} href={l.url} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-primary-50 text-primary-700 text-sm font-semibold hover:bg-primary-100 transition dark:bg-primary-500/15">
                {l.label || 'Zjistit více v nápovědě Google'} <ExternalLink className="w-3.5 h-3.5" />
              </a>
            ))}
          </div>
        )}
        {!text && links.length === 0 && (
          <p className="text-sm text-gray-500">K této kontrole nejsou další detaily.</p>
        )}
      </div>
    </div>,
    document.body
  )
}

function LighthouseAudits({ abc, cats }: { abc: any; cats: any }) {
  const [open, setOpen] = useState<string | null>('performance')
  const [modalAudit, setModalAudit] = useState<any | null>(null)
  const order = ['performance', 'accessibility', 'best-practices', 'seo']
  const catTitle: Record<string, string> = {
    performance: 'Výkon', accessibility: 'Přístupnost', 'best-practices': 'Doporučené postupy', seo: 'SEO',
  }
  const auditIcon = (a: any) => {
    if (a.score === null || a.score_display_mode === 'informative') return <span className="w-4 h-4 mt-0.5 shrink-0 rounded-full bg-gray-300 inline-block" />
    if (a.score >= 0.9) return <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
    if (a.score >= 0.5) return <AlertTriangle className="w-4 h-4 text-yellow-500 mt-0.5 shrink-0" />
    return <XCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
  }
  return (
    <Card title="Detailní audity">
      <div className="space-y-2">
        {order.filter((k) => abc[k]?.length).map((k) => {
          const audits = abc[k]
          const failed = audits.filter((a: any) => a.score !== null && a.score < 0.9)
          const passed = audits.filter((a: any) => a.score === null || a.score >= 0.9)
          const isOpen = open === k
          const catScore = cats[k]?.score
          return (
            <div key={k} className="border border-gray-100 rounded-xl overflow-hidden">
              <button onClick={() => setOpen(isOpen ? null : k)} className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition">
                <span className="flex items-center gap-2 font-medium text-gray-800">
                  {catScore != null && (
                    <span className={clsx('text-sm font-bold', catScore >= 90 ? 'text-green-600' : catScore >= 50 ? 'text-yellow-600' : 'text-red-600')}>{catScore}</span>
                  )}
                  {catTitle[k] || k}
                </span>
                <span className="flex items-center gap-2 text-xs">
                  {failed.length > 0 && <span className="text-red-600 font-medium">{failed.length} k řešení</span>}
                  <span className="text-green-600">{passed.length} OK</span>
                  {isOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                </span>
              </button>
              {isOpen && (
                <div className="px-4 pb-2 divide-y divide-gray-50">
                  {[...failed, ...passed].map((a: any) => (
                    <div key={a.id} className="py-2 text-sm">
                      <button
                        type="button"
                        onClick={() => a.description && setModalAudit(a)}
                        className={clsx('w-full flex items-start gap-2 text-left transition-colors', a.description ? 'cursor-pointer hover:text-primary-700' : 'cursor-default')}
                        title={a.description ? 'Zobrazit vysvětlení a nápovědu' : undefined}
                      >
                        {auditIcon(a)}
                        <span className="flex-1 text-gray-700">{a.title}</span>
                        {a.display_value && <span className="text-xs text-gray-500 shrink-0 text-right">{a.display_value}</span>}
                        {a.description && <HelpCircle className="w-3.5 h-3.5 text-gray-400 mt-0.5 shrink-0" />}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>
      {modalAudit && <LighthouseAuditModal audit={modalAudit} onClose={() => setModalAudit(null)} />}
    </Card>
  )
}

function CruxResult({ data, url }: { data: any; url?: string }) {
  const initStrategy: 'mobile' | 'desktop' = data?.form_factor === 'DESKTOP' ? 'desktop' : 'mobile'
  const [strategy, setStrategy] = useState<'mobile' | 'desktop'>(initStrategy)
  const [cache, setCache] = useState<Record<string, any>>({})
  const [loadingStrategy, setLoadingStrategy] = useState<'mobile' | 'desktop' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const inFlight = useRef<Set<string>>(new Set())

  useEffect(() => {
    if (data && !data.error) {
      const s = data.form_factor === 'DESKTOP' ? 'desktop' : 'mobile'
      setCache({ [s]: data }); setStrategy(s)
    } else setCache({})
    setError(null)
  }, [data])

  // inFlight + cache → každá strategie poběží max 1×. bg=true = tiché přednačtení.
  const loadStrategy = async (s: 'mobile' | 'desktop', bg = false) => {
    if (!url || cache[s] || inFlight.current.has(s)) return
    inFlight.current.add(s)
    setLoadingStrategy(s); setError(null)
    try {
      const { data: res } = await api.post('/tools/crux', { url, strategy: s })
      if (res?.error) { if (!bg) setError(res.message) } else setCache((c) => ({ ...c, [s]: res }))
    } catch (e: any) { if (!bg) setError(e?.response?.data?.message || 'Chyba.') }
    finally { inFlight.current.delete(s); setLoadingStrategy((cur) => (cur === s ? null : cur)) }
  }

  const switchTo = (s: 'mobile' | 'desktop') => { setStrategy(s); if (!cache[s]) loadStrategy(s) }

  // Přednačti druhou strategii na pozadí, jakmile dorazí první výsledek — přepnutí je pak okamžité.
  useEffect(() => {
    if (!url || !data || data.error) return
    const other: 'mobile' | 'desktop' = data.form_factor === 'DESKTOP' ? 'mobile' : 'desktop'
    const id = setTimeout(() => { loadStrategy(other, true) }, 600)
    return () => clearTimeout(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, url])

  const active = cache[strategy]
  const tabs = (
    <div className="inline-flex rounded-xl border border-gray-200 bg-gray-50 p-1 mb-4">
      {(['mobile', 'desktop'] as const).map((s) => (
        <button key={s} onClick={() => switchTo(s)}
          className={clsx('flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-medium transition',
            strategy === s ? 'bg-white shadow-sm text-primary-700' : 'text-gray-500 hover:text-gray-700')}>
          {s === 'mobile' ? <Smartphone className="w-4 h-4" /> : <BarChart3 className="w-4 h-4" />}
          {s === 'mobile' ? 'Mobil' : 'Desktop'}
        </button>
      ))}
    </div>
  )

  if (data?.error) return <Card title="Chrome UX Report">{tabs}<p className="text-sm text-red-600">{data.message}</p></Card>
  // Cache zobraz okamžitě — i když na pozadí dobíhá jiná strategie (jinak by hotová
  // strategie po přepnutí zpět vypadala, že se načítá znovu).
  if (!active) {
    const isLoading = loadingStrategy === strategy
    return <Card title="Chrome UX Report">{tabs}
      <div className="flex items-center gap-2 text-sm text-gray-500 py-6 justify-center">
        {isLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Načítám reálná data…</> : (error || 'Bez dat')}
      </div></Card>
  }

  const ratingColor = (r: string) => r === 'good' ? 'text-green-600' : r === 'ni' ? 'text-yellow-600' : 'text-red-600'
  const fmt = (m: any) => m.unit === 'ms' ? (m.p75 >= 1000 ? (m.p75 / 1000).toFixed(2) + ' s' : Math.round(m.p75) + ' ms') : m.p75.toFixed(3)

  return (
    <Card title={`Chrome UX Report — ${strategy === 'desktop' ? 'Desktop' : 'Mobil'}`}>
      {tabs}
      {!active.has_data ? (
        <p className="text-sm text-gray-500 py-4">Pro tento web nejsou v CrUX dostupná data (málo návštěvníků za posledních 28 dní). Zkuste Lighthouse pro laboratorní měření.</p>
      ) : (
        <>
          <p className="text-xs text-gray-500 mb-4">Reálná data od návštěvníků Chrome ({active.level === 'origin' ? 'celý web' : 'tato URL'}, posledních 28 dní). 75. percentil.</p>
          <div className="space-y-4">
            {active.metrics.map((m: any) => (
              <div key={m.id}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-700">{m.label}</span>
                  <span className={clsx('text-sm font-semibold', ratingColor(m.rating))}>{fmt(m)}</span>
                </div>
                <div className="flex h-2.5 rounded-full overflow-hidden bg-gray-100">
                  <div className="bg-green-500" style={{ width: `${m.good}%` }} title={`Dobré: ${m.good}%`} />
                  <div className="bg-yellow-400" style={{ width: `${m.ni}%` }} title={`Vyžaduje zlepšení: ${m.ni}%`} />
                  <div className="bg-red-500" style={{ width: `${m.poor}%` }} title={`Špatné: ${m.poor}%`} />
                </div>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-4 text-xs text-gray-500">
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-green-500" /> Dobré</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-yellow-400" /> Vyžaduje zlepšení</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-red-500" /> Špatné</span>
          </div>
        </>
      )}
    </Card>
  )
}

function SafeBrowsingResult({ data }: { data: any }) {
  if (data?.error) return <Card title="Safe Browsing"><p className="text-sm text-red-600">{data.message}</p></Card>
  const safe = data.safe
  const labels: Record<string, string> = {
    MALWARE: 'Malware', SOCIAL_ENGINEERING: 'Phishing / sociální inženýrství',
    UNWANTED_SOFTWARE: 'Nežádoucí software', POTENTIALLY_HARMFUL_APPLICATION: 'Potenciálně škodlivá aplikace',
  }
  return (
    <Card title="Google Safe Browsing">
      <div className={clsx('flex items-center gap-3 p-4 rounded-xl', safe ? 'bg-green-50' : 'bg-red-50')}>
        {safe ? <CheckCircle className="w-8 h-8 text-green-600 shrink-0" /> : <XCircle className="w-8 h-8 text-red-600 shrink-0" />}
        <div>
          <p className={clsx('font-semibold', safe ? 'text-green-700' : 'text-red-700')}>
            {safe ? 'Web je čistý' : 'Web označen jako nebezpečný!'}
          </p>
          <p className="text-xs text-gray-600">
            {safe ? 'Google neeviduje žádné hrozby (malware, phishing, nežádoucí software).' : 'Google detekoval následující hrozby:'}
          </p>
        </div>
      </div>
      {!safe && (
        <div className="mt-3 space-y-2">
          {data.threats.map((thr: any, i: number) => (
            <div key={i} className="flex items-center gap-2 text-sm text-red-700">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <span>{labels[thr.type] || thr.type}</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  )
}

function IndexationResult({ data }: { data: any }) {
  const { t } = useTranslation()
  const title = t('tres.indexability')
  if (data?.error) return <Card title={title}><p className="text-sm text-red-600">{data.message}</p></Card>

  const signals: any[] = data.signals || []
  const indexable: boolean = !!data.indexable

  return (
    <Card title={title}>
      <div className={clsx('flex items-center gap-3 p-4 rounded-xl mb-5',
        indexable ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800')}>
        {indexable ? <CheckCircle className="w-6 h-6 shrink-0" /> : <XCircle className="w-6 h-6 shrink-0" />}
        <div>
          <p className="font-semibold">{indexable ? t('tres.idx_can') : t('tres.idx_cannot')}</p>
          <p className="text-sm opacity-80">
            {indexable ? t('tres.idx_can_desc') : t('tres.idx_cannot_desc', { count: data.blockers })}
          </p>
        </div>
      </div>

      <div className="space-y-2">
        {signals.map((s: any, i: number) => {
          const Icon = s.status === 'block' ? XCircle : s.status === 'warn' ? AlertTriangle : CheckCircle
          const color = s.status === 'block' ? 'text-red-500' : s.status === 'warn' ? 'text-yellow-500' : 'text-green-500'
          return (
            <div key={i} className="flex items-start gap-3 py-2 border-b border-gray-100 last:border-0">
              <Icon className={clsx('w-5 h-5 shrink-0 mt-0.5', color)} />
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-800">{s.label}</p>
                <p className="text-xs text-gray-500">{s.detail}</p>
              </div>
            </div>
          )
        })}
      </div>

      <div className="mt-5 pt-4 border-t border-gray-100">
        <p className="text-xs text-gray-500 mb-2">{t('tres.idx_count_hint')}</p>
        <div className="flex flex-wrap gap-2">
          <a href={data.google_search_url} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-xl bg-gradient-to-r from-primary-600 to-primary-500 text-white hover:opacity-90">
            <ExternalLink className="w-4 h-4" />
            {t('tres.idx_open_domain', { domain: data.domain })}
          </a>
          <a href={data.google_page_url} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-xl border border-gray-200 text-gray-700 hover:bg-gray-50">
            <ExternalLink className="w-4 h-4" />
            {t('tres.idx_open_page')}
          </a>
        </div>
      </div>
    </Card>
  )
}

function LlmsTxtResult({ data }: { data: any }) {
  const { t } = useTranslation()
  const title = t('tres.llms_title', { defaultValue: 'llms.txt generátor' })
  if (data?.error) return <Card title={title}><p className="text-sm text-red-600">{data.message}</p></Card>
  if (!data?.content) return null

  const copy = () => { navigator.clipboard.writeText(data.content); toast.success(t('tres.llms_copied', { defaultValue: 'Zkopírováno' })) }
  const download = () => {
    const blob = new Blob([data.content], { type: 'text/plain;charset=utf-8' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = 'llms.txt'
    a.click()
    URL.revokeObjectURL(a.href)
  }

  return (
    <Card title={title}>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-800">{data.site_name}</p>
          <p className="text-xs text-gray-500">
            {t('tres.llms_found', { main: data.main_count, extra: data.extra_count, defaultValue: '{{main}} hlavních + {{extra}} dalších stránek' })}
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={copy} className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-xl border border-gray-200 text-gray-700 hover:bg-gray-50">
            <FileText className="w-4 h-4" /> {t('tres.llms_copy', { defaultValue: 'Kopírovat' })}
          </button>
          <button onClick={download} className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-xl bg-gradient-to-r from-primary-600 to-primary-500 text-white hover:opacity-90">
            <FileDown className="w-4 h-4" /> {t('tres.llms_download', { defaultValue: 'Stáhnout llms.txt' })}
          </button>
        </div>
      </div>

      <pre className="text-xs bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl p-4 overflow-x-auto whitespace-pre-wrap break-words max-h-96 text-gray-700 dark:text-gray-200">{data.content}</pre>

      <div className="mt-4 flex items-start gap-2 text-xs text-gray-500 bg-indigo-50 dark:bg-indigo-900/20 p-3 rounded-xl">
        <Sparkles className="w-4 h-4 shrink-0 text-primary-500 mt-0.5" />
        <span>{t('tres.llms_hint', { path: data.install_path, defaultValue: 'Nahrajte soubor do kořene webu jako {{path}}, pomáhá AI vyhledávačům (ChatGPT, Perplexity) pochopit strukturu webu.' })}</span>
      </div>
    </Card>
  )
}

function RedirectsResult({ data }: { data: any }) {
  const { t } = useTranslation()
  return (
    <>
      <Card title={t('tres.redirects')}>
        <div className="flex items-center gap-4 mb-6">
          <ScoreBadge score={data.score} />
          <span className="text-gray-500">{t('tres.chain')} {data.chain_length} {data.chain_length === 1 ? t('tres.step_one') : t('tres.steps_few')}</span>
          {data.has_loop && <span className="text-red-600 font-medium">{t('tres.loop_detected')}</span>}
        </div>

        <div className="space-y-2 mb-6">
          {data.chain?.map((step: any, i: number) => (
            <div key={i} className={clsx(
              'p-3 rounded-lg border text-sm',
              step.type === 'final' ? 'bg-green-50 border-green-200' :
              step.type === 'loop' ? 'bg-red-50 border-red-200' :
              step.type === 'error' ? 'bg-red-50 border-red-200' :
              'bg-yellow-50 border-yellow-200'
            )}>
              <div className="flex items-center justify-between">
                <span className="truncate font-mono text-xs">{step.url}</span>
                <span className={clsx('text-xs font-bold ml-2 shrink-0',
                  step.status === 200 ? 'text-green-600' :
                  step.status === 301 ? 'text-blue-600' :
                  step.status === 302 ? 'text-yellow-600' : 'text-red-600'
                )}>{step.status || 'ERR'}</span>
              </div>
              {step.error && <p className="text-xs text-red-500 mt-1">{step.error}</p>}
            </div>
          ))}
        </div>

        {data.http_redirect && (
          <div className="p-3 rounded-lg bg-gray-50 text-sm mb-3">
            <span className="text-gray-500">HTTP→HTTPS: </span>
            {data.http_redirect.redirects_to_https
              ? <span className="text-green-600 font-medium">{t('tres.redirects_https')}</span>
              : <span className="text-red-600 font-medium">{t('tres.no_redirect_https')}</span>}
          </div>
        )}
        {data.www_redirect && !data.www_redirect.error && (
          <div className="p-3 rounded-lg bg-gray-50 text-sm">
            <span className="text-gray-500">www vs non-www: </span>
            {data.www_redirect.redirects
              ? <span className="text-green-600 font-medium">{t('tres.redirects_w')} ({data.www_redirect.status})</span>
              : <span className="text-yellow-600 font-medium">{t('tres.both_work')} ({data.www_redirect.status})</span>}
          </div>
        )}
        <IssuesList issues={data.issues} />
      </Card>
    </>
  )
}

function ContentResult({ data }: { data: any }) {
  const { t } = useTranslation()
  const headings = data.headings || {}
  const hList = headings.list || []
  return (
    <>
      {/* Basic metrics */}
      <Card title={t('tres.content_analysis')}>
        <div className="flex items-center gap-6 mb-6">
          <div className="text-center">
            <ScoreBadge score={data.score} />
            <p className="text-xs text-gray-500 mt-1">{t('tres.content_label')}</p>
          </div>
          <div className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="text-center"><p className="text-xl font-bold text-gray-900">{data.word_count}</p><p className="text-xs text-gray-500">{t('tres.words_label')}</p></div>
            <div className="text-center"><p className="text-xl font-bold text-gray-900">{data.reading_time_min} min</p><p className="text-xs text-gray-500">{t('tres.reading_time')}</p></div>
            <div className="text-center">
              <p className={clsx('text-xl font-bold', data.readability_score >= 60 ? 'text-green-600' : data.readability_score >= 40 ? 'text-yellow-600' : 'text-red-600')}>
                {data.readability_score}
              </p>
              <p className="text-xs text-gray-500">{t('tres.readability')}</p>
            </div>
            <div className="text-center"><p className="text-xl font-bold text-gray-900">{data.sentence_count}</p><p className="text-xs text-gray-500">{t('tres.sentences')}</p></div>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
          <div className="px-3 py-2 rounded-lg bg-gray-50 text-center text-xs"><p className="font-semibold text-sm">{data.paragraph_count}</p><p>{t('tres.paragraphs')}</p></div>
          <div className="px-3 py-2 rounded-lg bg-gray-50 text-center text-xs"><p className="font-semibold text-sm">{data.avg_words_per_sentence}</p><p>{t('tres.words_per_sentence')}</p></div>
          <div className="px-3 py-2 rounded-lg bg-gray-50 text-center text-xs"><p className="font-semibold text-sm">{data.readability_level}</p><p>{t('tres.level')}</p></div>
          <div className="px-3 py-2 rounded-lg bg-gray-50 text-center text-xs"><p className="font-semibold text-sm">{headings.total || 0}</p><p>{t('tres.headings_col')}</p></div>
          <div className="px-3 py-2 rounded-lg bg-gray-50 text-center text-xs"><p className="font-semibold text-sm">{headings.keyword_coverage ?? 0}%</p><p>{t('tres.kw_in_headings')}</p></div>
        </div>
        <IssuesList issues={data.issues} />
      </Card>

      {/* Heading structure */}
      {hList.length > 0 && (
        <Card title={`${t('tres.heading_structure')} (${headings.total})`}>
          <div className="flex flex-wrap gap-2 mb-4">
            {Object.entries(headings.counts || {}).map(([level, count]: [string, any]) => (
              <span key={level} className={clsx(
                'text-xs px-2.5 py-1 rounded-full font-medium',
                level === '1' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'
              )}>
                H{level}: {count}x
              </span>
            ))}
          </div>
          <div className="space-y-1.5">
            {hList.map((h: any, i: number) => (
              <div key={i} className={clsx(
                'flex items-start gap-2 py-1.5 px-3 rounded-lg text-sm',
                h.skipped ? 'bg-red-50' : h.level === 1 ? 'bg-purple-50' : 'bg-gray-50'
              )} style={{ paddingLeft: `${(h.level - 1) * 16 + 12}px` }}>
                <span className={clsx(
                  'text-xs font-bold shrink-0 w-7 text-center py-0.5 rounded',
                  h.level === 1 ? 'bg-purple-200 text-purple-800' :
                  h.level === 2 ? 'bg-blue-100 text-blue-700' :
                  'bg-gray-200 text-gray-600'
                )}>
                  H{h.level}
                </span>
                <span className={clsx('flex-1 truncate', h.skipped && 'text-red-700')}>
                  {h.text || <span className="text-gray-300 italic">{t('tres.empty_h')}</span>}
                </span>
                <span className="text-xs text-gray-400 shrink-0">{h.length} {t('card.chars')}</span>
                {h.keywords_found?.length > 0 && (
                  <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded shrink-0">KW</span>
                )}
                {h.length_status === 'long' && (
                  <span className="text-xs bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded shrink-0">{t('tres.long_h')}</span>
                )}
              </div>
            ))}
          </div>
          {headings.issues?.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <IssuesList issues={headings.issues.map((i: any) => i.message)} />
            </div>
          )}
        </Card>
      )}

      {/* Keyword placement matrix */}
      {data.keyword_placement?.length > 0 && (
        <Card title={t('tres.kw_placement')}>
          <p className="text-xs text-gray-500 mb-3">{t('tres.kw_placement_desc')}</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b text-xs">
                  <th className="pb-2 font-medium">{t('tres.word_col')}</th>
                  <th className="pb-2 font-medium text-center">{t('tres.density_col')}</th>
                  <th className="pb-2 font-medium text-center">Title</th>
                  <th className="pb-2 font-medium text-center">H1</th>
                  <th className="pb-2 font-medium text-center">Meta Desc</th>
                  <th className="pb-2 font-medium text-center">{t('tres.first_paragraph')}</th>
                  <th className="pb-2 font-medium text-center">{t('tres.headings_col')}</th>
                  <th className="pb-2 font-medium text-center">{t('tres.score_col')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {data.keyword_placement.map((kp: any, i: number) => (
                  <tr key={i}>
                    <td className="py-2 font-medium">{kp.word}</td>
                    <td className="py-2 text-center">
                      <span className={clsx('text-xs font-bold px-2 py-0.5 rounded-full',
                        kp.density_status === 'over' ? 'bg-red-100 text-red-700' :
                        kp.density_status === 'ideal' ? 'bg-green-100 text-green-700' :
                        'bg-gray-100 text-gray-600'
                      )}>{kp.density}%</span>
                    </td>
                    {['in_title', 'in_h1', 'in_meta_desc', 'in_first_paragraph', 'in_headings'].map((key) => (
                      <td key={key} className="py-2 text-center">
                        {kp[key] ? <CheckCircle className="w-4 h-4 text-green-500 mx-auto" /> : <XCircle className="w-4 h-4 text-gray-300 mx-auto" />}
                      </td>
                    ))}
                    <td className="py-2 text-center">
                      <span className={clsx('text-xs font-bold',
                        kp.score >= 4 ? 'text-green-600' : kp.score >= 2 ? 'text-yellow-600' : 'text-red-600'
                      )}>{kp.score}/{kp.max_score}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-400 mt-3">{t('tres.kw_density_hint')}</p>
        </Card>
      )}

      {/* Top keywords with density */}
      {data.top_keywords?.length > 0 && (
        <Card title={t('tres.kw_density')}>
          <div className="space-y-2">
            {data.top_keywords.map((kw: any, i: number) => (
              <div key={i} className="flex items-center gap-3 text-sm">
                <span className="w-6 text-center text-gray-400 font-mono text-xs">{i + 1}</span>
                <span className="flex-1 font-medium text-gray-900">{kw.word}</span>
                <span className="text-gray-500">{kw.count}x</span>
                <span className={clsx('text-xs w-16 text-right font-medium',
                  kw.status === 'over' ? 'text-red-600' :
                  kw.status === 'ideal' ? 'text-green-600' :
                  'text-gray-400'
                )}>{kw.density}%</span>
                <div className="w-28 bg-gray-100 rounded-full h-2 relative">
                  <div className={clsx('h-2 rounded-full',
                    kw.status === 'over' ? 'bg-red-500' :
                    kw.status === 'ideal' ? 'bg-green-500' :
                    'bg-gray-400'
                  )} style={{ width: `${Math.min(100, kw.density * 20)}%` }} />
                  {/* Ideal range indicator 1-3% */}
                  <div className="absolute top-0 h-2 border-l border-r border-green-400 opacity-30" style={{ left: '20%', width: '40%' }} />
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 flex items-center gap-4 text-xs text-gray-400">
            <span className="flex items-center gap-1"><span className="w-3 h-2 bg-green-500 rounded-sm" /> {t('tres.density_ideal')}</span>
            <span className="flex items-center gap-1"><span className="w-3 h-2 bg-red-500 rounded-sm" /> {t('tres.density_over')}</span>
          </div>
        </Card>
      )}

      {/* Top phrases (bigrams) */}
      {data.top_phrases?.length > 0 && (
        <Card title={t('tres.top_phrases')}>
          <div className="space-y-2">
            {data.top_phrases.map((p: any, i: number) => (
              <div key={i} className="flex items-center gap-3 text-sm">
                <span className="w-6 text-center text-gray-400 font-mono text-xs">{i + 1}</span>
                <span className="flex-1 font-medium text-gray-900">{p.phrase}</span>
                <span className="text-gray-500">{p.count}x</span>
                <span className="text-xs text-gray-400 w-16 text-right">{p.density}%</span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </>
  )
}

function TechnologyResult({ data }: { data: any }) {
  const { t } = useTranslation()
  const categories = new Map<string, any[]>()
  data.technologies?.forEach((t: any) => {
    const list = categories.get(t.category) || []
    list.push(t)
    categories.set(t.category, list)
  })

  return (
    <Card title={t('tres.tech_title', { count: data.total || 0 })}>
      {data.total === 0 ? (
        <p className="text-gray-400 text-sm">{t('tres.no_tech')}</p>
      ) : (
        <div className="space-y-4">
          {Array.from(categories.entries()).map(([cat, techs]) => (
            <div key={cat}>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{cat}</p>
              <div className="flex flex-wrap gap-2">
                {techs.map((t: any, i: number) => (
                  <span key={i} className="px-3 py-1.5 bg-primary-50 text-primary-700 rounded-lg text-sm font-medium">{t.name}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      {data.headers && (
        <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-3 gap-3 text-xs text-gray-500">
          {data.headers.server && <div><span className="text-gray-400">Server:</span> {data.headers.server}</div>}
          {data.headers.powered_by && <div><span className="text-gray-400">Powered by:</span> {data.headers.powered_by}</div>}
          {data.headers.content_type && <div><span className="text-gray-400">Content-Type:</span> {data.headers.content_type}</div>}
        </div>
      )}
    </Card>
  )
}

function AccessibilityResult({ data }: { data: any }) {
  const { t } = useTranslation()
  return (
    <Card title={t('tres.accessibility_wcag')}>
      <div className="flex items-center gap-6 mb-6">
        <div className="text-center">
          <ScoreBadge score={data.score} />
          <p className="text-xs text-gray-500 mt-1">{t('tres.accessibility')}</p>
        </div>
        <div className="flex gap-4 text-sm">
          <span className="text-green-600 font-medium">{data.passed} {t('tres.passed')}</span>
          <span className="text-red-600 font-medium">{data.failed} {t('tres.failed')}</span>
          <span className="text-gray-400">{t('tres.of_total', { total: data.total_checks })}</span>
        </div>
      </div>
      <div className="space-y-3">
        {Object.entries(data.checks || {}).map(([key, val]: [string, any]) => (
          <div key={key} className={clsx('p-4 rounded-xl border', val.pass ? 'bg-green-50/50 border-green-100' : 'bg-red-50/50 border-red-100')}>
            <div className="flex items-start gap-3">
              <StatusBadge pass={val.pass} />
              <div className="flex-1">
                <p className="font-medium text-sm text-gray-900">{val.label}</p>
                <p className="text-sm text-gray-600 mt-0.5">{val.message}</p>
                {!val.pass && val.recommendation && (
                  <p className="text-sm text-gray-700 leading-relaxed mt-2 p-2.5 bg-amber-50 rounded-lg border border-amber-200">
                    <span className="font-bold text-amber-700">{t('common.recommendation')}:</span> {val.recommendation}
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  )
}

function WcagResult({ data }: { data: any }) {
  const { t } = useTranslation()
  const s = data.summary || {}
  const principles: { key: string; label: string }[] = [
    { key: 'perceivable', label: t('wcagres.perceivable', { defaultValue: 'Vnímatelnost' }) },
    { key: 'operable', label: t('wcagres.operable', { defaultValue: 'Ovladatelnost' }) },
    { key: 'understandable', label: t('wcagres.understandable', { defaultValue: 'Srozumitelnost' }) },
    { key: 'robust', label: t('wcagres.robust', { defaultValue: 'Robustnost' }) },
  ]
  const statusStyle: Record<string, string> = {
    pass: 'bg-green-50/50 border-green-100',
    fail: 'bg-red-50/50 border-red-100',
    warning: 'bg-amber-50/50 border-amber-100',
    review: 'bg-slate-50 border-slate-200',
  }
  const statusBadge: Record<string, { cls: string; label: string }> = {
    pass: { cls: 'bg-green-100 text-green-700', label: t('wcagres.pass', { defaultValue: 'Splněno' }) },
    fail: { cls: 'bg-red-100 text-red-700', label: t('wcagres.fail', { defaultValue: 'Nesplněno' }) },
    warning: { cls: 'bg-amber-100 text-amber-700', label: t('wcagres.warning', { defaultValue: 'Varování' }) },
    review: { cls: 'bg-slate-200 text-slate-600', label: t('wcagres.review', { defaultValue: 'K ověření' }) },
  }
  const crit: any[] = data.criteria || []
  return (
    <Card title={data.standard || 'WCAG 2.2 AA'}>
      <div className="flex items-center gap-6 mb-3 flex-wrap">
        <div className="text-center">
          <ScoreBadge score={data.score} />
          <p className="text-xs text-gray-500 mt-1">WCAG 2.2 AA</p>
        </div>
        <div className="flex gap-2 text-sm flex-wrap">
          {[
            { n: s.pass, label: t('wcagres.pass', { defaultValue: 'Splněno' }), cls: 'bg-green-50 text-green-700 ring-green-200 dark:bg-green-500/10 dark:text-green-300 dark:ring-green-500/20', num: 'bg-green-600 text-white' },
            { n: s.fail, label: t('wcagres.fail', { defaultValue: 'Nesplněno' }), cls: 'bg-red-50 text-red-700 ring-red-200 dark:bg-red-500/10 dark:text-red-300 dark:ring-red-500/20', num: 'bg-red-600 text-white' },
            { n: s.warning, label: t('wcagres.warning', { defaultValue: 'Varování' }), cls: 'bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20', num: 'bg-amber-500 text-white' },
            { n: s.review, label: t('wcagres.review', { defaultValue: 'K ověření' }), cls: 'bg-slate-100 text-slate-600 ring-slate-200 dark:bg-white/5 dark:text-slate-300 dark:ring-white/10', num: 'bg-slate-500 text-white' },
          ].map((b) => (
            <span key={b.label} className={clsx('inline-flex items-center gap-1.5 pl-1 pr-2.5 py-1 rounded-full ring-1 font-medium', b.cls)}>
              <span className={clsx('inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full text-xs font-bold tabular-nums', b.num)}>{b.n}</span>
              {b.label}
            </span>
          ))}
        </div>
      </div>
      {data.note && <p className="text-xs text-gray-400 italic mb-5">{data.note}</p>}
      <div className="space-y-6">
        {principles.map((p) => {
          const items = crit.filter((c) => c.principle === p.key)
          if (items.length === 0) return null
          return (
            <div key={p.key}>
              <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-2">{p.label}</h3>
              <div className="space-y-3">
                {items.map((c) => (
                  <div key={c.sc} className={clsx('p-4 rounded-xl border', statusStyle[c.status] || 'bg-gray-50 border-gray-100')}>
                    <div className="flex items-start gap-3">
                      <span className={clsx('shrink-0 text-[10px] font-bold px-2 py-1 rounded-full', statusBadge[c.status]?.cls)}>
                        {statusBadge[c.status]?.label}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm text-gray-900">
                          <span className="text-gray-400 font-mono mr-1.5">{c.sc}</span>
                          {c.name}
                          <span className="ml-1.5 text-[10px] font-bold text-primary-600 bg-primary-50 px-1.5 py-0.5 rounded">{c.level}</span>
                        </p>
                        <p className="text-sm text-gray-600 mt-0.5">{c.message}</p>
                        {c.status !== 'pass' && c.recommendation && (
                          <p className="text-sm text-gray-700 leading-relaxed mt-2 p-2.5 bg-amber-50 rounded-lg border border-amber-200">
                            <span className="font-bold text-amber-700">{t('common.recommendation')}:</span> {c.recommendation}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )
        })}
      </div>
    </Card>
  )
}

function UxResult({ data }: { data: any }) {
  const { t } = useTranslation()
  const { open: openKb } = useKnowledge()
  const navIds: string[] = Object.keys(data.checks || {}).filter(hasKb)
  return (
    <Card title={t('tools.ux.name')}>
      <div className="flex items-center gap-6 mb-2">
        <div className="text-center">
          <ScoreBadge score={data.score} />
          <p className="text-xs text-gray-500 mt-1">UX / UI</p>
        </div>
        <div className="flex gap-4 text-sm">
          <span className="text-green-600 font-medium">{data.passed} {t('tres.passed')}</span>
          <span className="text-red-600 font-medium">{data.failed} {t('tres.failed')}</span>
          <span className="text-gray-400">{t('tres.of_total', { total: data.total_checks })}</span>
        </div>
      </div>
      {data.note && <p className="text-xs text-gray-400 italic mb-5">{data.note}</p>}
      <div className="space-y-3">
        {Object.entries(data.checks || {}).map(([key, val]: [string, any]) => (
          <div key={key} className={clsx('p-4 rounded-xl border', val.pass ? 'bg-green-50/50 border-green-100' : 'bg-amber-50/50 border-amber-100')}>
            <div className="flex items-start gap-3">
              <StatusBadge pass={val.pass} />
              <div className="flex-1">
                <p className="font-medium text-sm text-gray-900">{val.label}</p>
                <p className="text-sm text-gray-600 mt-0.5">{val.message}</p>
                {!val.pass && val.recommendation && (
                  <p className="text-sm text-gray-700 leading-relaxed mt-2 p-2.5 bg-amber-50 rounded-lg border border-amber-200">
                    <span className="font-bold text-amber-700">{t('common.recommendation')}:</span> {val.recommendation}
                  </p>
                )}
                {hasKb(key) && (
                  <button onClick={() => openKb(key, navIds)}
                    className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-primary-600 hover:text-primary-700 hover:gap-2 transition-all">
                    <BookOpen className="w-3.5 h-3.5" /> {t('kb.learn_more')}
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  )
}

function CookiesResult({ data }: { data: any }) {
  const { t } = useTranslation()
  return (
    <>
      <Card title="Cookies & GDPR">
        <div className="flex items-center gap-4 mb-6">
          <ScoreBadge score={data.score} />
          <span className="text-gray-500">/ 100</span>
        </div>
        <div className="space-y-3">
          {Object.entries(data.checks || {}).map(([key, val]: [string, any]) => (
            <div key={key} className={clsx('p-4 rounded-xl border',
              val.pass ? 'bg-green-50/50 border-green-100' : val.uncertain ? 'bg-amber-50/50 border-amber-200' : 'bg-yellow-50/50 border-yellow-100')}>
              <div className="flex items-start gap-3">
                {val.uncertain
                  ? <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                  : <StatusBadge pass={val.pass} />}
                <div className="flex-1">
                  <p className="font-medium text-sm text-gray-900">{val.label}{val.uncertain && <span className="ml-2 text-xs font-normal text-amber-600">nešlo ověřit staticky</span>}</p>
                  <p className="text-sm text-gray-600 mt-0.5">{val.message}</p>
                  {!val.pass && !val.uncertain && val.recommendation && (
                    <p className="text-sm text-gray-700 leading-relaxed mt-2 p-2.5 bg-amber-50 rounded-lg border border-amber-200">
                      <span className="font-bold text-amber-700">{t('common.recommendation')}:</span> {val.recommendation}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        <IssuesList issues={data.issues} />
      </Card>
      {data.cookies?.length > 0 && (
        <Card title={t('tres.server_cookies', { count: data.cookies.length })}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b text-xs">
                  <th className="pb-2 font-medium">{t('tres.name_col')}</th>
                  <th className="pb-2 font-medium text-center">Secure</th>
                  <th className="pb-2 font-medium text-center">HttpOnly</th>
                  <th className="pb-2 font-medium text-center">SameSite</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {data.cookies.map((c: any, i: number) => (
                  <tr key={i}>
                    <td className="py-2 font-mono text-xs">{c.name}</td>
                    <td className="py-2 text-center">{c.secure ? <CheckCircle className="w-4 h-4 text-green-500 mx-auto" /> : <XCircle className="w-4 h-4 text-red-400 mx-auto" />}</td>
                    <td className="py-2 text-center">{c.httponly ? <CheckCircle className="w-4 h-4 text-green-500 mx-auto" /> : <XCircle className="w-4 h-4 text-red-400 mx-auto" />}</td>
                    <td className="py-2 text-center">{c.samesite ? <CheckCircle className="w-4 h-4 text-green-500 mx-auto" /> : <XCircle className="w-4 h-4 text-red-400 mx-auto" />}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </>
  )
}

function DnsResult({ data }: { data: any }) {
  const { t } = useTranslation()
  const ssl = data.ssl || {}
  return (
    <>
      <Card title={t('tres.ssl_cert')}>
        <div className="flex items-center gap-4 mb-6">
          <ScoreBadge score={data.score} />
          {ssl.valid
            ? <span className="text-green-600 font-medium">{t('tres.cert_valid')}</span>
            : <span className="text-red-600 font-medium">{ssl.error || t('tres.cert_invalid')}</span>}
        </div>
        {ssl.valid && (
          <div className="grid grid-cols-2 gap-4 text-sm mb-4">
            <div><span className="text-gray-500">{t('tres.issuer')}</span> <span className="font-medium">{ssl.issuer}</span></div>
            <div><span className="text-gray-500">{t('tres.subject')}</span> <span className="font-medium">{ssl.subject}</span></div>
            <div><span className="text-gray-500">{t('tres.valid_from')}</span> <span className="font-medium">{ssl.valid_from}</span></div>
            <div><span className="text-gray-500">{t('tres.valid_to')}</span> <span className={clsx('font-medium', (ssl.days_left ?? 0) < 30 ? 'text-red-600' : 'text-green-600')}>{ssl.valid_to}</span></div>
            <div><span className="text-gray-500">{t('tres.remaining')}</span> <span className={clsx('font-bold', (ssl.days_left ?? 0) < 30 ? 'text-red-600' : 'text-green-600')}>{ssl.days_left} {t('tres.days')}</span></div>
            {ssl.signature_algorithm && <div><span className="text-gray-500">{t('tres.algorithm')}</span> <span className="font-medium">{ssl.signature_algorithm}</span></div>}
          </div>
        )}
        {ssl.san?.length > 0 && (
          <div className="mt-2">
            <p className="text-xs text-gray-500 mb-1">{t('tres.san_domains')}</p>
            <div className="flex flex-wrap gap-1">
              {ssl.san.slice(0, 10).map((s: string, i: number) => (
                <span key={i} className="text-xs bg-gray-100 px-2 py-0.5 rounded">{s}</span>
              ))}
              {ssl.san.length > 10 && <span className="text-xs text-gray-400">+{ssl.san.length - 10} {t('tres.more')}</span>}
            </div>
          </div>
        )}
      </Card>

      <Card title={t('tres.dns_records')}>
        {Object.entries(data.dns || {}).map(([type, records]: [string, any]) => (
          <div key={type} className="mb-4 last:mb-0">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{type}</p>
            <div className="space-y-1">
              {records.map((r: string, i: number) => (
                <p key={i} className="text-sm font-mono text-gray-700 bg-gray-50 px-3 py-1.5 rounded truncate">{r}</p>
              ))}
            </div>
          </div>
        ))}
        {data.email_auth && (
          <div className="mt-4 pt-4 border-t border-gray-100 flex gap-4">
            <span className={clsx('text-xs px-2 py-1 rounded-full font-medium', data.email_auth.spf ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700')}>
              SPF: {data.email_auth.spf ? 'OK' : t('tres.missing')}
            </span>
            <span className={clsx('text-xs px-2 py-1 rounded-full font-medium', data.email_auth.dmarc ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700')}>
              DMARC: {data.email_auth.dmarc ? 'OK' : t('tres.missing')}
            </span>
          </div>
        )}
      </Card>

      {data.issues?.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <IssuesList issues={data.issues} />
        </div>
      )}
    </>
  )
}

function BrokenLinksResult({ data }: { data: any }) {
  const { t } = useTranslation()
  const [filter, setFilter] = useState<'all' | 'broken' | 'redirect' | 'ok'>('all')
  const links = (data.links || []).filter((l: any) => filter === 'all' || l.type === filter)

  return (
    <Card title={t('tres.link_check')}>
      <div className="flex items-center gap-6 mb-6">
        <ScoreBadge score={data.score} />
        <div className="flex gap-4 text-sm">
          <span className="text-green-600 font-medium">{data.ok} OK</span>
          <span className="text-red-600 font-medium">{data.broken} {t('tres.dead')}</span>
          <span className="text-yellow-600 font-medium">{data.redirected} {t('tres.redirected')}</span>
          <span className="text-gray-400">{data.skipped} {t('tres.skipped')}</span>
        </div>
      </div>

      <div className="flex gap-2 mb-4">
        {(['all', 'broken', 'redirect', 'ok'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={clsx('px-3 py-1.5 rounded-lg text-xs font-medium transition',
              filter === f ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            )}
          >
            {f === 'all' ? `${t('tres.all')} (${data.total})` : f === 'broken' ? `${t('tres.broken_f')} (${data.broken})` : f === 'redirect' ? `Redirect (${data.redirected})` : `OK (${data.ok})`}
          </button>
        ))}
      </div>

      <div className="space-y-1.5 max-h-[500px] overflow-y-auto">
        {links.map((l: any, i: number) => (
          <div key={i} className={clsx(
            'flex items-center gap-3 px-3 py-2 rounded-lg text-sm',
            l.type === 'broken' ? 'bg-red-50' : l.type === 'redirect' ? 'bg-yellow-50' : 'bg-gray-50'
          )}>
            <span className={clsx('text-xs font-bold w-8 text-center shrink-0',
              l.type === 'broken' ? 'text-red-600' : l.type === 'redirect' ? 'text-yellow-600' : 'text-green-600'
            )}>{l.status || 'ERR'}</span>
            <span className={clsx('text-xs px-1.5 py-0.5 rounded shrink-0',
              l.internal ? 'bg-blue-100 text-blue-600' : 'bg-gray-200 text-gray-500'
            )}>{l.internal ? 'INT' : 'EXT'}</span>
            <div className="flex-1 min-w-0">
              <p className="truncate text-xs font-mono text-gray-700">{l.url}</p>
              {l.anchor && <p className="text-xs text-gray-400 truncate">{l.anchor}</p>}
              {l.error && <p className="text-xs text-red-500 truncate">{l.error}</p>}
            </div>
            <a href={l.url} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-primary-600 shrink-0">
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        ))}
        {links.length === 0 && <p className="text-center text-gray-400 py-4">{t('tres.no_links_filter')}</p>}
      </div>
    </Card>
  )
}

function CompareResult({ data }: { data: any }) {
  const { t } = useTranslation()
  const a = data.site_a || {}
  const b = data.site_b || {}
  return (
    <>
      {/* Score comparison header */}
      <Card title={t('tres.site_compare')}>
        <div className="grid grid-cols-3 gap-4 items-center mb-6">
          <div className="text-center">
            <ScoreBadge score={a.score} />
            <p className="text-sm font-medium text-gray-900 mt-1 truncate">{a.domain}</p>
          </div>
          <div className="text-center text-gray-400 text-2xl font-light">vs</div>
          <div className="text-center">
            <ScoreBadge score={b.score} />
            <p className="text-sm font-medium text-gray-900 mt-1 truncate">{b.domain}</p>
          </div>
        </div>

        {/* Categories */}
        {Object.entries(data.categories || {}).map(([cat, rows]: [string, any]) => (
          <div key={cat} className="mb-6 last:mb-0">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{cat}</h4>
            <div className="space-y-1">
              {rows.map((row: any, i: number) => {
                const aWins = row.lower_better ? (row.a_val ?? 0) < (row.b_val ?? 0) : (row.a_val ?? 0) > (row.b_val ?? 0)
                const bWins = row.lower_better ? (row.b_val ?? 0) < (row.a_val ?? 0) : (row.b_val ?? 0) > (row.a_val ?? 0)
                const hasCmp = row.a_val !== undefined && row.b_val !== undefined && row.a_val !== row.b_val
                return (
                  <div key={i} className="grid grid-cols-3 gap-4 py-2 px-3 rounded-lg hover:bg-gray-50 text-sm">
                    <div className={clsx('text-right', hasCmp && aWins && 'text-green-600 font-medium', hasCmp && bWins && 'text-red-500')}>
                      {row.a}
                    </div>
                    <div className="text-center text-gray-500 text-xs self-center">{row.label}</div>
                    <div className={clsx('text-left', hasCmp && bWins && 'text-green-600 font-medium', hasCmp && aWins && 'text-red-500')}>
                      {row.b}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        ))}
      </Card>

      {/* Keywords comparison */}
      <div className="grid grid-cols-2 gap-4">
        {[{ title: a.domain, keywords: data.top_keywords_a }, { title: b.domain, keywords: data.top_keywords_b }].map(({ title, keywords }, idx) => (
          <Card key={idx} title={`${t('rank.keywords')} — ${title}`}>
            <div className="space-y-1.5">
              {(keywords || []).map((kw: any, i: number) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  <span className="text-xs text-gray-400 w-4">{i + 1}</span>
                  <span className="flex-1 font-medium truncate">{kw.word}</span>
                  <span className="text-xs text-gray-400">{kw.count}x</span>
                  <span className="text-xs text-gray-400 w-12 text-right">{kw.density}%</span>
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>
    </>
  )
}

function BulkResult({ data }: { data: any }) {
  const { t } = useTranslation()
  return (
    <Card title={t('tres.bulk_title', { count: data.total })}>
      <div className="flex gap-4 text-sm mb-4">
        <span className="text-green-600 font-medium">{data.ok} OK</span>
        <span className="text-yellow-600 font-medium">{data.with_issues} {t('tres.with_issues')}</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500 border-b text-xs">
              <th className="pb-2 font-medium">URL</th>
              <th className="pb-2 font-medium w-14">Status</th>
              <th className="pb-2 font-medium">Title</th>
              <th className="pb-2 font-medium w-14">ms</th>
              <th className="pb-2 font-medium w-14">KB</th>
              <th className="pb-2 font-medium">{t('common.issues')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {(data.results || []).map((r: any, i: number) => (
              <tr key={i} className={clsx(r.issues?.length > 0 && 'bg-yellow-50/50')}>
                <td className="py-2 text-xs truncate max-w-[200px] text-primary-600">{r.url}</td>
                <td className="py-2">
                  <span className={clsx('text-xs font-bold',
                    r.status === 200 ? 'text-green-600' : r.status > 0 ? 'text-red-600' : 'text-gray-400'
                  )}>{r.status || '—'}</span>
                </td>
                <td className="py-2 text-xs truncate max-w-[200px] text-gray-600">{r.title || '—'}</td>
                <td className="py-2 text-xs text-gray-500">{r.load_time ?? '—'}</td>
                <td className="py-2 text-xs text-gray-500">{r.size_kb ?? '—'}</td>
                <td className="py-2">
                  {r.issues?.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {r.issues.map((issue: string, j: number) => (
                        <span key={j} className="text-[10px] bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded">{issue}</span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-xs text-green-600">OK</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  )
}

function GeoResult({ data }: { data: any }) {
  const { t } = useTranslation()
  return (
    <>
      <Card title={t('tres.geo_title')}>
        <div className="flex items-center gap-6 mb-6">
          <ScoreBadge score={data.score} />
          <div className="flex flex-wrap gap-4 text-sm">
            <span className="text-gray-500">AI boty: <strong>{data.summary?.ai_bots_allowed}</strong></span>
            <span className="text-gray-500">E-E-A-T: <strong>{data.summary?.eeat_signals}</strong></span>
            <span className="text-gray-500">{t('tres.citability')}: <strong>{data.summary?.cite_features}</strong></span>
            <span className="text-gray-500">llms.txt: {data.summary?.llms_txt ? <span className="text-green-600 font-medium">{t('common.yes')}</span> : <span className="text-red-500">{t('common.no')}</span>}</span>
          </div>
        </div>
      </Card>

      {data.checks?.ai_bots && (
        <Card title={t('tres.ai_bot_access')}>
          <p className="text-sm text-gray-500 mb-3">{data.checks.ai_bots.message}</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
            {Object.entries(data.checks.ai_bots.bots || {}).map(([bot, info]: [string, any]) => (
              <div key={bot} className={clsx(
                'flex items-center gap-2 px-3 py-2 rounded-lg text-sm',
                info.allowed ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
              )}>
                {info.allowed ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                <div>
                  <p className="font-medium text-xs">{bot}</p>
                  <p className="text-[10px] opacity-70">{info.name}</p>
                </div>
              </div>
            ))}
          </div>
          {data.checks.ai_bots.status !== 'pass' && (
            <p className="text-sm text-gray-700 leading-relaxed p-2.5 bg-amber-50 rounded-lg border border-amber-200"><span className="font-bold text-amber-700">{t('common.recommendation')}:</span> {data.checks.ai_bots.recommendation}</p>
          )}
        </Card>
      )}

      {Object.entries(data.checks || {}).filter(([k]) => k !== 'ai_bots').map(([key, check]: [string, any]) => {
        const titles: Record<string, string> = {
          llms_txt: 'llms.txt', citability: t('tres.citability'), eeat: t('tres.eeat'),
          ai_schema: t('tres.ai_schema'), freshness: t('tres.freshness'),
        }
        return (
          <Card key={key} title={titles[key] || key}>
            <div className="flex items-start gap-3 mb-3">
              <StatusBadge pass={check.status === 'pass'} />
              <div className="flex-1">
                <p className="text-sm text-gray-700">{check.message}</p>
                {(check.signals || check.features || check.schemas)?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {(check.signals || check.features || check.schemas).map((item: string, i: number) => (
                      <span key={i} className="text-xs bg-primary-50 text-primary-700 px-2 py-0.5 rounded-full">{item}</span>
                    ))}
                  </div>
                )}
                {check.content && (
                  <pre className="mt-2 text-xs bg-gray-50 dark:bg-white/5 p-3 rounded-lg max-h-40 overflow-y-auto text-gray-600 dark:text-gray-300 whitespace-pre-wrap break-words max-w-full">{check.content}</pre>
                )}
              </div>
              <span className={clsx('text-xs font-bold', check.score > 7 ? 'text-green-600' : check.score > 3 ? 'text-yellow-600' : 'text-red-600')}>
                {check.score}b
              </span>
            </div>
            {check.status !== 'pass' && check.recommendation && (
              <p className="text-sm text-gray-700 leading-relaxed p-2.5 bg-amber-50 rounded-lg border border-amber-200">
                <span className="font-bold text-amber-700">{t('common.recommendation')}:</span> {check.recommendation}
              </p>
            )}
          </Card>
        )
      })}
    </>
  )
}

/**
 * Vykreslení výsledku jednoho nástroje — sdílené mezi stránkou Nástroje a sdíleným
 * snapshotem (/s/{token}), aby snapshot ukázal CELÝ výsledek, ne jen URL a skóre.
 */
export function ToolResultView({ tool, data, url }: { tool: string; data: any; url?: string }) {
  switch (tool) {
    case 'robots': return <RobotsResult data={data} />
    case 'sitemap': return <SitemapResult data={data} />
    case 'schema': return <SchemaResult data={data} />
    case 'serp': return <SerpResult data={data} />
    case 'hreflang': return <HreflangResult data={data} />
    case 'mobile': return <MobileResult data={data} />
    case 'security': return <SecurityResult data={data} />
    case 'pagespeed': return <PageSpeedResult data={data} />
    case 'lighthouse': return <LighthouseResult data={data} url={url} />
    case 'crux': return <CruxResult data={data} url={url} />
    case 'safe-browsing': return <SafeBrowsingResult data={data} />
    case 'indexation': return <IndexationResult data={data} />
    case 'redirects': return <RedirectsResult data={data} />
    case 'content': return <ContentResult data={data} />
    case 'technology': return <TechnologyResult data={data} />
    case 'accessibility': return <AccessibilityResult data={data} />
    case 'wcag': return <WcagResult data={data} />
    case 'ux': return <UxResult data={data} />
    case 'cookies': return <CookiesResult data={data} />
    case 'dns': return <DnsResult data={data} />
    case 'broken-links': return <BrokenLinksResult data={data} />
    case 'compare': return <CompareResult data={data} />
    case 'bulk': return <BulkResult data={data} />
    case 'geo': return <GeoResult data={data} />
    case 'llms-txt': return <LlmsTxtResult data={data} />
    default: return null
  }
}
